import { useState, useCallback, useMemo, memo, useEffect, useRef } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  BackgroundVariant,
  Panel,
  MarkerType,
  ReactFlowProvider,
  ConnectionLineType,
} from "reactflow"
import "reactflow/dist/style.css"

import { X, MessageSquare, Zap, Check, Loader2, RefreshCw, ChevronRight, Settings, Key, AlertCircle, Info, ChevronDown } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Textarea } from "@/shared/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/shared/components/ui/select"
import { Badge } from "@/shared/components/ui/badge"
import { Switch } from "@/shared/components/ui/switch"
import { ChatPanel } from "@/shared/components/chat"
import type { ChatMessageType as ChatMessage, ExecutionNode, RouteInfo } from "@/shared/components/chat"
import { useProjectStore } from "@/modules/agent/store/projectStore"
import { useModels } from "@/modules/agent/hooks/useModels"
import { useTools } from "@/modules/agent/hooks/useTools"
import { TokenManager } from "@/shared/services/api"
import { ProjectStatus } from "@/shared/types/enums"
import type { AgentProject } from "@/modules/agent/types/project"
import { FlowApiService } from "@/modules/agent/services/flow-api"
import { ProjectApiService } from "@/modules/agent/services/project-api"
import { listScenes, type SceneConfig } from "@/modules/identity/services/identity-api"
import { useVoiceConfig } from "@/modules/agent/hooks/useVoiceConfig"
import { CompanionAgentNode } from "@/modules/agent/components/flow-nodes/CompanionAgentNode"

// 导入拆分的节点组件
import { EntryNode, AgentNode, PersonaAgentNode, StructuredAgentNode, OutputNode, RouterNode, BinderNode, HermesAgentNode } from './components/nodes'
// 导入拆分的面板组件
import { ConfigPanel, EdgeConfigPanel, ProjectSkillsPanel } from './components/panels'
// 导入拆分的工具栏组件
import { ProjectToolbar, NodeToolbar } from './components/toolbar'
// 导入拆分的对话框组件
import { ProjectSettingsDialog, RegenerateConfirmDialog, DeleteConfirmDialog } from './components/dialogs'
import { ExecutionContext as SharedExecutionContext } from "@/modules/agent/components/flow-nodes/shared/ExecutionContext"
import { useVAD } from "@/modules/agent/hooks/useVAD"
import { useEnergyVAD } from "@/modules/agent/hooks/useEnergyVAD"
import { useAudioPlayer, type AudioFormat } from "@/modules/agent/hooks/useAudioPlayer"
import { useVoiceWebSocket } from "@/modules/agent/hooks/useVoiceWebSocket"
import { useToast } from "@/shared/hooks/useToast"
import { ToastContainer } from "@/shared/components/ui/toast"
import { Mic, Volume2, Radio, Smartphone, Clock } from "lucide-react"
import { Slider } from "@/shared/components/ui/slider"
import { VoicePicker } from "@/modules/agent/components/VoicePicker"

// 条件类型
interface Condition {
  id: string
  name: string        // 条件名称（显示在连线上）
  condition: string   // 详细判断条件（多行文本）
  target: string      // 目标 Agent ID
}

// 执行状态 Context（用于节点动画）— 使用共享定义，供外部节点组件共享
const ExecutionContext = SharedExecutionContext

// 动画事件类型
interface AnimationEvent {
  type: 'flow_start' | 'node_enter' | 'flow_end'
  nodeId?: string
  timestamp: number
}

// 动画调度间隔（毫秒）
const ANIMATION_INTERVAL = 300












// 节点类型映射 - 在组件外部定义，避免重新创建
// v10.0 新增 persona_agent（身份模拟专用）
// v11.3 新增 structured_agent（真正的 Agent Loop）
// v13.0 新增 binder（并行分支编排器）
const NODE_TYPES = {
  entry: EntryNode,
  agent: AgentNode,
  persona_agent: PersonaAgentNode,  // v10.0 新增
  structured_agent: StructuredAgentNode,  // v11.3 新增
  companion_agent: CompanionAgentNode,    // v12.0 新增：陪伴智能体
  binder: BinderNode,                     // v13.0 新增：并行分支编排器
  hermes_agent: HermesAgentNode,          // v16.0 新增：原生智能体
  router: RouterNode,
  output: OutputNode,
}

// 默认边样式 - 在组件外部定义，避免重新创建
const DEFAULT_EDGE_OPTIONS = {
  style: { stroke: "#94a3b8", strokeWidth: 2 },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
}

// 连接线样式 - 在组件外部定义，避免重新创建
const CONNECTION_LINE_STYLE = {
  stroke: '#94a3b8',
  strokeWidth: 2,
}

// 网格配置 - 在组件外部定义，避免重新创建
const SNAP_GRID: [number, number] = [5, 5]

// ====== 节点默认值 ======
// 各节点类型的完整默认值。用于：
// 1. 创建新节点时（addXxxNode）
// 2. 从后端加载节点后补全缺失字段（applyNodeDefaults）
// 策略：{ ...defaults, ...serverData } — 服务器值优先，默认值只填补 undefined 字段
const NODE_TYPE_DEFAULTS: Record<string, Record<string, unknown>> = {
  entry: {
    label: '用户输入',
    enableVoice: false,
    enable_user_prompt: false,
    user_prompt_templates: {
      user: '',
      iot_data: '',
      schedule: '',
    },
  },
  agent: {
    label: '基础智能体',
    role: '',
    tool_call_mode: 'json_text',
    async_mode: false,
    one_shot_mode: false,
    silent_mode: false,
    user_context: false,
    temperature: 0.7,
    tools: [],
  },
  persona_agent: {
    label: '身份模拟',
    scene_id: 'general',
    tool_call_mode: 'json_text',
    silent_mode: false,
    temperature: 0.7,
    tools: [],
  },
  structured_agent: {
    label: '自主智能体',
    max_steps: 10,
    timeout_seconds: 120,
    safety_enabled: true,
    skills_enabled: true,
    memory_recall_enabled: true,
    user_context: false,
    tool_call_mode: 'function_calling',
    enableCodeInterpreter: false,
    compaction_enabled: false,
    show_reasoning: true,
    show_tool_progress: true,
    silent_mode: false,
    temperature: 0.7,
    tools: [],
  },
  companion_agent: {
    label: '陪伴智能体',
    safety_enabled: true,
    tool_call_mode: 'json_text',
    identity_rules: '',
    max_evolution_chars: 4000,
    silent_mode: false,
    user_context: false,
    temperature: 0.7,
    tools: [],
  },
  hermes_agent: {                        // v16.0: Skill-driven native agent
    label: '原生智能体',
    model: '',
    max_steps: 30,
    skills_path: 'skills/',
    global_skills: true,
    silent_mode: false,
  },
  router: {
    label: '条件判断',
    temperature: 0.7,
    conditions: [],
  },
  output: {
    label: '回复用户',
    audioFormat: 'mp3',
    enableTTS: false,
    enable_mood_learning: false,
  },
  binder: {
    label: '并行编排器',
    strategy: 'parallel',
    wait_for: 'all',
    timeout: 30,
  },
}

// 已知的数字类型字段（处理 DynamoDB 存储为字符串的历史数据）
const NUMERIC_FIELDS = new Set([
  'max_steps', 'timeout_seconds', 'temperature', 'max_tokens',
  'max_context_tokens', 'keep_recent_messages', 'speed', 'sampleRate',
  'skills_match_threshold', 'skills_top_k', 'max_evolution_chars',
  'max_memory_file_chars', 'compaction_threshold', 'compaction_top_k',
  'memory_file_compaction_threshold', 'timeout',  // v13.0 新增：Binder节点超时
])

// 通用节点默认值合并函数
// 在 loadFlowConfig 加载节点后、setNodes 之前调用
function applyNodeDefaults(node: Node): Node {
  const defaults = NODE_TYPE_DEFAULTS[node.type || '']
  if (!defaults) return node

  // 合并：默认值在下，服务器数据在上（服务器值优先）
  const mergedData = { ...defaults, ...node.data }

  // 修正数字字段类型（DynamoDB 可能存为字符串或 null）
  for (const key of Object.keys(mergedData)) {
    if (NUMERIC_FIELDS.has(key)) {
      const value = mergedData[key]
      // 处理字符串类型
      if (typeof value === 'string') {
        mergedData[key] = Number(value)
      }
      // 处理 null/undefined/NaN - 使用默认值
      else if (value == null || (typeof value === 'number' && isNaN(value))) {
        mergedData[key] = defaults[key]
      }
    }
  }

  return { ...node, data: mergedData }
}

// 默认节点（新项目）
const getDefaultNodes = (): Node[] => [
  {
    id: "entry",
    type: "entry",
    position: { x: 50, y: 200 },
    data: { ...NODE_TYPE_DEFAULTS.entry },
  },
  {
    id: "output",
    type: "output",
    position: { x: 500, y: 200 },
    data: { ...NODE_TYPE_DEFAULTS.output },
  },
]

// 默认边（新项目）
const getDefaultEdges = (): Edge[] => []

// 右侧配置面板组件 - 独立出来避免影响主画布渲染




// ==================== Entry 节点配置组件 ====================
interface EntryNodeConfigProps {
  selectedNode: Node
  onUpdateNodeData: (field: string, value: unknown) => void
  onOpenPromptEditor: (field: string, content: string, title: string, description: string) => void
}

const EntryNodeConfig = memo(({ selectedNode, onUpdateNodeData, onOpenPromptEditor }: EntryNodeConfigProps) => {
  // 加载语音配置
  const { asrProviders, defaultAsrProvider, isLoading: isLoadingVoiceConfig } = useVoiceConfig()
  
  return (
    <>
      <div className="space-y-2">
        <Label>入口名称</Label>
        <Input 
          value={selectedNode.data.label || ''} 
          onChange={(e) => onUpdateNodeData('label', e.target.value)}
        />
      </div>
      
      {/* V6.0: 语音输入配置 */}
      <div className="pt-3 mt-3 border-t space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mic className="h-4 w-4 text-blue-500" />
            <Label htmlFor="enable-voice-switch" className="text-sm font-medium">
              语音输入
            </Label>
          </div>
          <Switch
            id="enable-voice-switch"
            checked={selectedNode.data.enableVoice ?? false}
            onCheckedChange={(checked) => onUpdateNodeData('enableVoice', checked)}
          />
        </div>
        <p className="text-xs text-slate-500">
          开启后支持语音输入，用户可通过麦克风与 Agent 对话
        </p>
        
        {/* ASR 配置（仅当语音输入开启时显示） */}
        {selectedNode.data.enableVoice && (
          <div className="space-y-3 pt-2">
            <div className="space-y-2">
              <Label className="text-sm">语音识别服务</Label>
              {isLoadingVoiceConfig ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  加载中...
                </div>
              ) : (
                <Select
                  value={selectedNode.data.asrProvider || defaultAsrProvider || 'aliyun'}
                  onValueChange={(value) => onUpdateNodeData('asrProvider', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择 ASR 服务" />
                  </SelectTrigger>
                  <SelectContent>
                    {asrProviders.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        <div className="flex items-center gap-2">
                          <span>{provider.name}</span>
                          {provider.default && (
                            <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                              默认
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* 自定义 ASR URL（当 custom 或 pet 时显示） */}
            {(selectedNode.data.asrProvider === 'custom' || selectedNode.data.asrProvider === 'pet') && (
              <div className="space-y-2">
                <Label className="text-sm">
                  {selectedNode.data.asrProvider === 'pet' ? 'Pet ASR URL' : '自定义 ASR URL'}
                </Label>
                <Input
                  value={selectedNode.data.customAsrUrl || ''}
                  onChange={(e) => onUpdateNodeData('customAsrUrl', e.target.value)}
                  placeholder={selectedNode.data.asrProvider === 'pet' 
                    ? "ws://8.130.92.113:8201/pet/ws"
                    : "wss://your-asr-service.com/ws"
                  }
                />
                <p className="text-xs text-slate-500">
                  WebSocket 地址
                </p>
              </div>
            )}
            
            {/* Pet ASR JWT Token（仅 pet 时显示） */}
            {selectedNode.data.asrProvider === 'pet' && (
              <div className="space-y-2">
                <Label className="text-sm">JWT Token</Label>
                <Input
                  type="password"
                  value={selectedNode.data.petAsrJwtToken || ''}
                  onChange={(e) => onUpdateNodeData('petAsrJwtToken', e.target.value)}
                  placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                />
                <p className="text-xs text-slate-500">
                  认证令牌（需包含 device_id）
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Prompt 配置 */}
      <div className="pt-3 mt-3 border-t space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-green-500" />
            <Label htmlFor="enable-user-prompt-switch" className="text-sm font-medium">
              用户提示词
            </Label>
          </div>
          <Switch
            id="enable-user-prompt-switch"
            checked={selectedNode.data.enable_user_prompt ?? false}
            onCheckedChange={(checked) => onUpdateNodeData('enable_user_prompt', checked)}
          />
        </div>
        <p className="text-xs text-slate-500">
          开启后可为不同触发类型配置专用的用户提示词模板
        </p>

        {/* User Prompt 模板配置（仅当开启时显示） */}
        {selectedNode.data.enable_user_prompt && (
          <div className="space-y-3 pt-2">
            <Label className="text-sm font-medium">User Prompt 模板</Label>
            
            {/* 用户输入模板 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-600">用户输入 (user)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => onOpenPromptEditor(
                    'user_prompt_templates.user',
                    selectedNode.data.user_prompt_templates?.user || '',
                    'User Prompt 模板 - 用户输入',
                    '用于用户直接输入时的提示词模板。可用变量: {mood}, {message}'
                  )}
                >
                  展开
                </Button>
              </div>
              <Textarea
                value={selectedNode.data.user_prompt_templates?.user || ''}
                onChange={(e) => onUpdateNodeData('user_prompt_templates.user', e.target.value)}
                placeholder="可用变量: {mood}, {message}"
                className="text-xs font-mono"
                rows={2}
              />
            </div>

            {/* IoT数据模板 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-600">IoT数据 (iot_data)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => onOpenPromptEditor(
                    'user_prompt_templates.iot_data',
                    selectedNode.data.user_prompt_templates?.iot_data || '',
                    'User Prompt 模板 - IoT数据',
                    '用于IoT设备数据触发时的提示词模板。可用变量: {message} 和IoT数据字段（如 {heart_rate}, {steps} 等）'
                  )}
                >
                  展开
                </Button>
              </div>
              <Textarea
                value={selectedNode.data.user_prompt_templates?.iot_data || ''}
                onChange={(e) => onUpdateNodeData('user_prompt_templates.iot_data', e.target.value)}
                placeholder="可用变量: {message}, {heart_rate}, {steps} 等IoT数据字段"
                className="text-xs font-mono"
                rows={2}
              />
            </div>

            {/* 定时任务模板 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-600">定时任务 (schedule)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => onOpenPromptEditor(
                    'user_prompt_templates.schedule',
                    selectedNode.data.user_prompt_templates?.schedule || '',
                    'User Prompt 模板 - 定时任务',
                    '用于定时任务触发时的提示词模板。可用变量: {message}, {trigger_time}, {schedule_name}'
                  )}
                >
                  展开
                </Button>
              </div>
              <Textarea
                value={selectedNode.data.user_prompt_templates?.schedule || ''}
                onChange={(e) => onUpdateNodeData('user_prompt_templates.schedule', e.target.value)}
                placeholder="可用变量: {message}, {trigger_time}, {schedule_name}"
                className="text-xs font-mono"
                rows={2}
              />
            </div>
          </div>
        )}
      </div>

    </>
  )
})
EntryNodeConfig.displayName = 'EntryNodeConfig'

// ==================== Output 节点配置组件 ====================
interface OutputNodeConfigProps {
  selectedNode: Node
  onUpdateNodeData: (field: string, value: unknown) => void
}

const OutputNodeConfig = memo(({ selectedNode, onUpdateNodeData }: OutputNodeConfigProps) => {
  // 加载语音配置
  const { 
    ttsProviders, 
    defaultTtsProvider, 
    audioFormats,
    getVoicesByProvider,
    getDefaultVoiceId,
    getDefaultAudioFormat,
    isLoading: isLoadingVoiceConfig 
  } = useVoiceConfig()
  
  // 当前选择的 TTS 提供商
  const currentProvider = selectedNode.data.ttsProvider || defaultTtsProvider || 'cosyvoice'
  // 获取当前提供商的音色列表
  const currentVoices = getVoicesByProvider(currentProvider)
  
  return (
    <>
      <div className="space-y-2">
        <Label>出口名称</Label>
        <Input 
          value={selectedNode.data.label || ''} 
          onChange={(e) => onUpdateNodeData('label', e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label>输出类型</Label>
        <Select 
          value={selectedNode.data.outputType || 'reply'} 
          onValueChange={(value) => onUpdateNodeData('outputType', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="选择输出类型" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="reply">回复用户</SelectItem>
            <SelectItem value="silent">静默结束</SelectItem>
            <SelectItem value="webhook">触发 Webhook</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* V6.0: TTS 语音输出配置 */}
      <div className="pt-3 mt-3 border-t space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Volume2 className="h-4 w-4 text-green-500" />
            <Label htmlFor="enable-tts-switch" className="text-sm font-medium">
              语音输出
            </Label>
          </div>
          <Switch
            id="enable-tts-switch"
            checked={selectedNode.data.enableTTS ?? false}
            onCheckedChange={(checked) => {
              onUpdateNodeData('enableTTS', checked)
              // 启用 TTS 时，确保设置默认的音频格式（避免 undefined 导致后端使用 PCM）
              if (checked && !selectedNode.data.audioFormat) {
                onUpdateNodeData('audioFormat', getDefaultAudioFormat())
              }
            }}
          />
        </div>
        <p className="text-xs text-slate-500">
          开启后 Agent 回复将转换为语音播放
        </p>
        
        {/* TTS 详细配置（仅当语音输出开启时显示） */}
        {selectedNode.data.enableTTS && (
          <div className="space-y-3 pt-2">
            {/* TTS 提供商 */}
            <div className="space-y-2">
              <Label className="text-sm">语音合成服务</Label>
              {isLoadingVoiceConfig ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  加载中...
                </div>
              ) : (
                <Select
                  value={currentProvider}
                  onValueChange={(value) => {
                    onUpdateNodeData('ttsProvider', value)
                    // 切换提供商时，重置音色为新提供商的默认音色
                    const newDefaultVoice = getDefaultVoiceId(value)
                    if (newDefaultVoice) {
                      onUpdateNodeData('defaultVoiceId', newDefaultVoice)
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择 TTS 服务" />
                  </SelectTrigger>
                  <SelectContent>
                    {ttsProviders.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        <div className="flex items-center gap-2">
                          <span>{provider.name}</span>
                          {provider.default && (
                            <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                              默认
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* 自定义 TTS URL（仅当选择 custom 时显示） */}
            {currentProvider === 'custom' && (
              <div className="space-y-2">
                <Label className="text-sm">自定义 TTS URL</Label>
                <Input
                  value={selectedNode.data.customTtsUrl || ''}
                  onChange={(e) => onUpdateNodeData('customTtsUrl', e.target.value)}
                  placeholder="wss://your-tts-service.com/ws"
                />
                <p className="text-xs text-slate-500">
                  WebSocket 地址，需兼容 Axiom TTS 协议
                </p>
              </div>
            )}
            
            {/* 音色选择（非 custom 时显示） */}
            {currentProvider !== 'custom' && currentVoices.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">默认音色</Label>
                <VoicePicker
                  value={selectedNode.data.defaultVoiceId || getDefaultVoiceId(currentProvider)}
                  onValueChange={(value) => onUpdateNodeData('defaultVoiceId', value)}
                  voices={currentVoices}
                  placeholder="选择音色"
                />
                <p className="text-xs text-slate-500">
                  Agent 可在回复中覆盖此音色
                </p>
              </div>
            )}
            
            {/* 语速调节 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">语速</Label>
                <span className="text-xs text-slate-500">
                  {(selectedNode.data.speed ?? 1.0).toFixed(1)}x
                </span>
              </div>
              <Slider
                value={selectedNode.data.speed ?? 1.0}
                onValueChange={(value) => onUpdateNodeData('speed', value)}
                min={0.5}
                max={2.0}
                step={0.1}
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>0.5x</span>
                <span>1.0x</span>
                <span>2.0x</span>
              </div>
            </div>
            
            {/* 音频格式 */}
            <div className="space-y-2">
              <Label className="text-sm">音频格式</Label>
              <Select
                value={selectedNode.data.audioFormat || getDefaultAudioFormat()}
                onValueChange={(value) => onUpdateNodeData('audioFormat', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择格式" />
                </SelectTrigger>
                <SelectContent>
                  {audioFormats.map((format) => (
                    <SelectItem key={format.id} value={format.id}>
                      <div className="flex items-center gap-2">
                        <span>{format.name}</span>
                        {format.default && (
                          <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                            默认
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* 采样率 */}
            <div className="space-y-2">
              <Label className="text-sm">采样率</Label>
              <Select
                value={String(selectedNode.data.sampleRate || 22050)}
                onValueChange={(value) => onUpdateNodeData('sampleRate', parseInt(value, 10))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择采样率" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="8000">8000 Hz (电话质量)</SelectItem>
                  <SelectItem value="16000">16000 Hz (宽带语音)</SelectItem>
                  <SelectItem value="22050">
                    <div className="flex items-center gap-2">
                      <span>22050 Hz</span>
                      <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">默认</Badge>
                    </div>
                  </SelectItem>
                  <SelectItem value="24000">24000 Hz (Opus 原生)</SelectItem>
                  <SelectItem value="44100">44100 Hz (CD 质量)</SelectItem>
                  <SelectItem value="48000">48000 Hz (专业音频)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {selectedNode.data.audioFormat === 'opus' 
                  ? 'Opus 格式会自动匹配最接近的支持采样率' 
                  : '更高采样率提供更好音质，但增加网络传输量'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* v12.0: 情绪学习配置 */}
      <div className="pt-3 mt-3 border-t space-y-3">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-sm">情绪学习</Label>
            <p className="text-xs text-slate-500">对话结束后分析用户情绪状态并记录</p>
          </div>
          <Switch
            checked={selectedNode.data.enable_mood_learning ?? false}
            onCheckedChange={(checked) => onUpdateNodeData('enable_mood_learning', checked)}
          />
        </div>
      </div>
    </>
  )
})
OutputNodeConfig.displayName = 'OutputNodeConfig'


// ==================== 主配置面板已迁移到独立组件 ====================


// Agent 配置类型
interface AgentConfig {
  id: string
  name: string
  role: string
  prompt: string
  model: string
  tools: string[]
}

// Router 配置类型
interface RouterConditionConfig {
  id: string
  condition: string
  target: string
}

interface RouterConfig {
  id: string
  name: string
  model: string  // 路由节点使用的模型
  conditions: RouterConditionConfig[]
}

// 流程执行路径节点
// ExecutionNode 类型已从 shared/components/chat 导入

// 测试面板组件
interface TestPanelProps {
  projectName: string
  projectId: string  // 项目ID，用于传递给后端
  isSaved: boolean   // 是否已保存，未保存时禁止测试
  onClose: () => void
  agents: AgentConfig[]
  routers: RouterConfig[]
  edges: Edge[]
  // V6.0 新增：Entry 节点语音配置
  entryNodeVoiceConfig?: {
    enableVoice: boolean
    asrProvider?: string
  }
  // 执行状态回调（用于更新流程图动画）
  onNodeActivate?: (nodeId: string | null) => void
  onNodeVisited?: (nodeId: string) => void
  onExecutionReset?: () => void
  
  // v8.2 新增：设备测试模式支持
  mode?: 'admin' | 'device'  // 默认 'admin'
  deviceTestConfig?: {
    deviceId: string
    deviceName: string
    externalToken: string
    tokenGetter: () => Promise<string | null>
  }
}

// 使用Agent Admin API（用于测试Agent流程）
// 注意：SSE 流式响应需要使用原生 fetch，所以这里直接使用环境变量
const API_BASE = import.meta.env.VITE_AGENT_ADMIN_API_URL || 'http://localhost:8001/api/v1'

const TestPanel = memo(({ projectName, projectId, isSaved: _isSaved, onClose, agents, routers, edges: _edges, entryNodeVoiceConfig, onNodeActivate, onNodeVisited, onExecutionReset, mode = 'admin', deviceTestConfig }: TestPanelProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [streamingContent, setStreamingContent] = useState("")
  const [executionPath, setExecutionPath] = useState<ExecutionNode[]>([])
  const [currentNode, setCurrentNode] = useState<string>("")
  const [routeInfo, setRouteInfo] = useState<RouteInfo | null>(null)
  
  // v8.1: 设备指令缓存（用于处理指令先于AI消息到达的情况）
  const [pendingDeviceCommand, setPendingDeviceCommand] = useState<{ action: string; [key: string]: unknown } | null>(null)
  
  // v11.3 新增：Structured Agent 可观测性状态
  // thinking 事件: {"type": "thinking", "step": 2, "node": "客服助手"}
  const [thinkingStep, setThinkingStep] = useState<{ step: number; node: string } | null>(null)
  // tool_progress 事件: {"type": "tool_progress", "tool": "get_weather", "description": "正在执行...", "step": 2}
  const [toolProgress, setToolProgress] = useState<{ tool: string; description: string; step: number } | null>(null)
  // v13.0 新增：reasoning 事件累积（流式推理过程）
  // reasoning 事件: {"type": "reasoning", "content": "...", "step": 1, "node": "助手"}
  const [reasoningContent, setReasoningContent] = useState<string>('')
  
  // v8.2: 性能指标（设备测试模式显示）
  const [metrics, setMetrics] = useState<{ ttft?: number; totalTime?: number }>({})
  const sendTimeRef = useRef<number>(0)
  const firstTokenTimeRef = useRef<number>(0)
  
  // Session ID：用于 Memory 功能的会话标识
  // 规范：UUID v4 格式（纯 UUID，不加前缀），由前端生成
  const [sessionId, setSessionId] = useState<string>(() => crypto.randomUUID())
  
  // Device ID：用于 IoT 设备控制场景
  const [deviceId, setDeviceId] = useState<string>('')
  const [showTestOptions, setShowTestOptions] = useState(false)

  // v10.0 新增：Identity ID（身份模拟所需）
  const [identityId, setIdentityId] = useState<string>('')

  // v8.1: API 版本选择（带 localStorage 持久化）
  const [apiVersion, setApiVersion] = useState<'v1' | 'v2'>(() => {
    try {
      const saved = localStorage.getItem(`axiom_admin_voice_api_version_${projectId}`)
      return (saved as 'v1' | 'v2') || 'v1'
    } catch {
      return 'v1'
    }
  })
  const [showArchInfo, setShowArchInfo] = useState(false)  // v2 架构说明卡片展开状态
  const [hasSeenV2Arch, setHasSeenV2Arch] = useState(() => {  // 是否已看过 v2 架构说明
    try {
      return localStorage.getItem(`has_seen_v2_arch_${projectId}`) === 'true'
    } catch {
      return false
    }
  })

  // v8.3: VAD Provider 选择（仅在 vad/vad_interruptible 模式下生效）
  const [vadProvider, setVadProvider] = useState<'client' | 'server'>(() => {
    try {
      const saved = localStorage.getItem(`axiom_vad_provider_${projectId}`)
      return (saved as 'client' | 'server') || 'client'
    } catch {
      return 'client'
    }
  })
  
  // V6.0: 语音功能状态
  // v8.2: 设备测试模式默认启用语音功能（设备测试主要就是测试语音对话）
  const isVoiceEnabled = mode === 'device' ? true : (entryNodeVoiceConfig?.enableVoice ?? false)
  // V7.0: Pet ASR 模式检测 - Pet 模式使用能量 VAD 而非 Silero VAD
  // 设备测试模式暂不支持 Pet ASR（需要后端配合）
  const isPetMode = mode === 'device' ? false : (entryNodeVoiceConfig?.asrProvider === 'pet')
  // off = 未选择, ptt = 按住说话, vad = 自动检测(暂停式), vad_interrupt = 自动检测(可打断)
  const [voiceInputMode, setVoiceInputMode] = useState<'off' | 'ptt' | 'vad' | 'vad_interrupt'>('off')
  
  // 🔍 调试：检查语音配置
  console.log('🎤 [Voice Debug] 语音配置状态:', {
    mode,
    entryNodeVoiceConfig,
    isVoiceEnabled,
    voiceInputMode,
  })
  
  // 🔍 监控voiceInputMode变化
  useEffect(() => {
    console.log('🎤 [Voice Debug] voiceInputMode 变化:', voiceInputMode)
  }, [voiceInputMode])
  const [showVoiceModePopover, setShowVoiceModePopover] = useState(false)  // 显示模式选择气泡
  // VAD 灵敏度（仅 vad_interrupt 模式使用，值越高越不容易误触发）
  const [vadSensitivity, setVadSensitivity] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(`axiom_vad_sensitivity_${projectId}`)
      return stored ? parseFloat(stored) : 0.7  // 默认 0.7（较高，避免 TTS 被误识别）
    } catch {
      return 0.7
    }
  })
  const [showRecordingOverlay, setShowRecordingOverlay] = useState(false)  // 显示全屏录音 overlay
  const [recordingCancelled, setRecordingCancelled] = useState(false)  // 是否取消录音（上滑取消）
  const [isRecording, setIsRecording] = useState(false)
  const [asrInterimText, setAsrInterimText] = useState('')  // ASR 中间结果
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const recordingStartYRef = useRef<number>(0)  // 录音开始时的 Y 坐标（用于判断上滑取消）
  const recordingCancelledRef = useRef<boolean>(false)  // 使用 ref 避免闭包问题
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null)  // 长按计时器（区分点击和长按）
  const voiceWsRef = useRef<ReturnType<typeof useVoiceWebSocket> | null>(null)  // WebSocket ref（避免闭包问题）
  const currentAssistantMsgIdRef = useRef<string | null>(null)  // 当前流式 Agent 消息 ID
  const currentUserMsgIdRef = useRef<string | null>(null)  // 当前用户语音消息 ID（用于 ASR 后更新）
  const ttsMsgIdRef = useRef<string | null>(null)  // TTS 音频要更新的消息 ID（独立于 flow 生命周期）
  const ttsAudioChunksRef = useRef<string[]>([])  // TTS 音频片段累积
  const ttsAudioFormatRef = useRef<AudioFormat>('mp3')  // TTS 音频格式
  const isInterruptingRef = useRef<boolean>(false)  // 打断状态：发送 stop 后设为 true，收到新 tts_start 后重置
  
  // V6.0: vad_interrupt 模式的等待 cancelled 状态
  const interruptStateRef = useRef<{
    isWaitingCancelled: boolean      // 是否在等待 cancelled 确认
    stopSentTime: number | null      // 发送 stop 的时间戳（用于计算延迟）
    audioBuffer: { audio: Float32Array; duration: number }[]  // 缓存的音频数据
    timeoutId: ReturnType<typeof setTimeout> | null  // 超时定时器
  }>({
    isWaitingCancelled: false,
    stopSentTime: null,
    audioBuffer: [],
    timeoutId: null,
  })
  
  // V6.0: 音频播放器 (TTS)
  const audioPlayer = useAudioPlayer({
    onPlayStart: () => {
      console.log('[Voice] TTS 开始播放')
      // AEC: 仅在 vad 模式下暂停（vad_interrupt 模式不暂停，依赖浏览器 AEC）
      if (voiceInputMode === 'vad') {
        vad.pause()
      }
      // vad_interrupt 模式：VAD 继续运行，用户说话时会触发打断
    },
    onPlayEnd: () => {
      console.log('[Voice] TTS 播放结束')
      // AEC: 仅在 vad 模式下恢复
      if (voiceInputMode === 'vad' && vad.isListening) {
        vad.resume()
      }
    },
  })
  
  // V6.0: WebSocket 语音通信
  // v8.2: 设备测试模式使用 runtime endpoint + 外部 token
  // v8.3: 添加 vadProvider 支持（客户端 VAD vs 云端 VAD）
  const voiceWs = useVoiceWebSocket({
    projectId,
    sessionId,
    deviceId: mode === 'device' ? deviceTestConfig?.deviceId : deviceId,
    // v8.3: 根据 voiceInputMode 设置 voiceMode
    voiceMode: voiceInputMode === 'ptt' ? 'push_to_talk' 
             : voiceInputMode === 'vad_interrupt' ? 'vad_interruptible' 
             : voiceInputMode === 'vad' ? 'vad' 
             : 'push_to_talk',  // off 时默认 push_to_talk
    vadProvider: vadProvider,  // v8.3: VAD 责任方
    apiVersion: mode === 'device' ? 'v2' : apiVersion,
    endpoint: mode === 'device' ? 'runtime' : 'admin',
    externalToken: mode === 'device' ? deviceTestConfig?.externalToken : undefined,
    tokenGetter: mode === 'device' ? deviceTestConfig?.tokenGetter : undefined,
    identityId: identityId.trim() || undefined,  // v10.0: 身份 ID
  }, {
    onASRInterim: (text) => {
      setAsrInterimText(text)
    },
    onASRFinal: (text) => {
      console.log('[Voice] ASR 最终结果:', text)
      setAsrInterimText('')
      // 更新已有的用户消息（如果有），否则创建新消息
      if (currentUserMsgIdRef.current) {
        setMessages(prev => prev.map(msg => 
          msg.id === currentUserMsgIdRef.current
            ? { ...msg, content: text, timestamp: new Date() }
            : msg
        ))
        currentUserMsgIdRef.current = null
      } else {
        // 兜底：如果没有占位符消息，创建新消息
        const userMessage: ChatMessage = {
          id: `msg-${Date.now()}`,
          role: "user",
          content: text,
          timestamp: new Date(),
        }
        setMessages(prev => [...prev, userMessage])
      }
    },
    onContent: (content, isStreaming) => {
      console.log('[Voice] 收到 content:', content, 'isStreaming:', isStreaming)
      
      // v8.2: 设备测试模式记录 TTFT
      if (mode === 'device' && !firstTokenTimeRef.current && sendTimeRef.current) {
        firstTokenTimeRef.current = Date.now()
        const ttft = firstTokenTimeRef.current - sendTimeRef.current
        setMetrics(prev => ({ ...prev, ttft }))
        console.log('[DeviceTest] 首包耗时 (TTFT):', ttft, 'ms')
      }
      
      // 收到第一个 content 时创建 Agent 消息
      if (!currentAssistantMsgIdRef.current) {
        const msgId = `msg-${Date.now()}`
        currentAssistantMsgIdRef.current = msgId
        const assistantMessage: ChatMessage = {
          id: msgId,
          role: "assistant",
          content: content,
          timestamp: new Date(),
          isStreaming: isStreaming !== false,  // 默认为流式输入中
          // v8.1: 如果有待处理的设备指令，立即附加
          ...(pendingDeviceCommand && { deviceCommand: pendingDeviceCommand })
        }
        setMessages(prev => [...prev, assistantMessage])
        
        // 清除待处理的设备指令
        if (pendingDeviceCommand) {
          console.log('[Voice] 已将待处理设备指令附加到AI消息:', pendingDeviceCommand)
          setPendingDeviceCommand(null)
        }
      } else {
        // 追加内容到现有消息（保持原始时间戳，不更新）
        setMessages(prev => prev.map(msg => 
          msg.id === currentAssistantMsgIdRef.current
            ? { ...msg, content: msg.content + content }
            : msg
        ))
      }
    },
    onTTSStart: (metadata) => {
      console.log('[Voice] TTS 开始, 元数据:', metadata)
      // 新一轮 TTS 开始，重置打断标志
      if (isInterruptingRef.current) {
        console.log('[Voice] 重置打断标志')
        isInterruptingRef.current = false
      }
      // 重置音频播放器的会话统计（抖动等）
      audioPlayer.resetSessionStats()
      // 设置音频元数据（格式、采样率、通道数、位深度）
      audioPlayer.setMetadata({
        format: metadata.format,
        sampleRate: metadata.sampleRate,
        channels: metadata.channels,
        bitDepth: metadata.bitDepth,
      })
      // 保存 TTS 目标消息 ID（优先使用当前 Agent 消息 ID，否则使用后端返回的 messageId）
      ttsMsgIdRef.current = currentAssistantMsgIdRef.current || metadata.messageId || null
      console.log('[Voice] TTS 目标消息 ID:', ttsMsgIdRef.current)
      // 记录音频格式，清空累积
      ttsAudioFormatRef.current = metadata.format
      ttsAudioChunksRef.current = []
    },
    onAudio: (audioBase64, format) => {
      // 打断期间丢弃残留音频
      if (isInterruptingRef.current) {
        console.log('[Voice] 丢弃打断期间的残留音频, 长度:', audioBase64.length)
        return
      }
      // 累积音频片段
      console.log('[Voice] 收到 TTS 音频, 格式:', format, '长度:', audioBase64.length)
      ttsAudioChunksRef.current.push(audioBase64)
      // 边收边播（元数据已在 onTTSStart 设置）
      audioPlayer.queueAudio(audioBase64)
    },
    onTTSEnd: () => {
      console.log('[Voice] TTS 结束, 合并音频片段数:', ttsAudioChunksRef.current.length)
      
      // 强制播放缓冲区中的剩余音频
      audioPlayer.flush()
      
      // 合并音频并更新消息（用于后续重播）
      if (ttsMsgIdRef.current && ttsAudioChunksRef.current.length > 0) {
        // 合并所有音频片段（简单拼接 Base64，实际播放时会分段解码）
        const mergedAudio = ttsAudioChunksRef.current.join('')
        // 估算时长（MP3 大约 128kbps，Base64 增加 33%）
        // 实际时长 ≈ base64Length * 0.75 / (128000 / 8) 秒
        const estimatedDuration = Math.round(mergedAudio.length * 0.75 / 16000)
        
        const targetMsgId = ttsMsgIdRef.current
        console.log('[Voice] 更新消息音频数据, msgId:', targetMsgId, '时长:', estimatedDuration)
        
        setMessages(prev => prev.map(msg => 
          msg.id === targetMsgId
            ? { 
                ...msg, 
                isStreaming: false,
                audioData: mergedAudio,
                audioFormat: ttsAudioFormatRef.current,
                audioDuration: estimatedDuration > 0 ? estimatedDuration : 1,
                timestamp: new Date(),
              }
            : msg
        ))
      } else {
        console.warn('[Voice] TTS 结束但无法更新消息:', {
          ttsMsgId: ttsMsgIdRef.current,
          audioChunksCount: ttsAudioChunksRef.current.length,
        })
      }
      // 清空累积
      ttsAudioChunksRef.current = []
      ttsMsgIdRef.current = null
    },
    onCancelled: (reason) => {
      // 🆕 后端确认取消，发送缓存的音频
      const state = interruptStateRef.current
      const stopSentTime = state.stopSentTime
      const now = Date.now()
      const delay = stopSentTime ? now - stopSentTime : 0
      
      console.log(`[Voice] ✅ 收到 cancelled 确认, reason: ${reason}, 延迟: ${delay}ms`)
      
      // 清除超时定时器
      if (state.timeoutId) {
        clearTimeout(state.timeoutId)
        state.timeoutId = null
      }
      
      // 如果有缓存的音频，立即发送
      if (state.audioBuffer.length > 0) {
        console.log(`[Voice] 📤 发送缓存的音频, 共 ${state.audioBuffer.length} 段`)
        flushInterruptAudioBuffer()
      }
      
      // 重置等待状态
      state.isWaitingCancelled = false
      state.stopSentTime = null
    },
    onFlowEvent: (event, data) => {
      // 处理流程事件（与 SSE 文字模式相同的逻辑）
      console.log('[Voice] Flow event:', event, data)
      if (event === 'flow_start') {
        console.log('🎯 [Voice] flow_start 事件，激活入口节点')
        
        // 🔧 修复连续打断问题：新的 flow_start 意味着新的对话轮开始
        // 需要清空旧的音频队列和重置打断标志，防止新音频被错误丢弃
        if (isInterruptingRef.current) {
          console.log('[Voice] flow_start: 重置打断标志')
          isInterruptingRef.current = false
        }
        // 停止并清空音频播放器中可能残留的旧音频
        audioPlayer.stop()
        
        setCurrentNode('entry')
        setExecutionPath([])  // 重置执行路径
        enqueueAnimationEvent({ type: 'flow_start', timestamp: Date.now() })
        // 重置当前消息 ID
        currentAssistantMsgIdRef.current = null
      } else if (event === 'node_enter') {
        const nodeId = (data.node_id as string) || 'unknown'
        const nodeName = (data.node_name as string) || nodeId
        const status = (data.status as string) || 'running'
        console.log('🎯 [Voice] node_enter 事件:', nodeId, nodeName, 'status:', status)
        if (nodeId && nodeId !== 'unknown') {
          setCurrentNode(nodeId)
          // 🔧 修复：按 node_id 查找并更新状态 - AgentNode 会发射两次 node_enter（running → completed）
          setExecutionPath(prev => {
            const existingIndex = prev.findIndex(item => item.node === nodeId)
            if (existingIndex >= 0) {
              // 节点已存在，更新状态
              const updated = [...prev]
              updated[existingIndex] = { 
                node: nodeId, 
                name: nodeName,
                status: status,
              }
              return updated
            }
            // 新节点，添加到路径
            return [...prev, { 
              node: nodeId, 
              name: nodeName,
              status: status,
            }]
          })
          // 🔧 修复动画混乱：只在 status=running 时激活动画
          // status=completed 的事件不入队动画，避免重复激活同一节点
          if (status === 'running') {
            enqueueAnimationEvent({ type: 'node_enter', nodeId, timestamp: Date.now() })
          } else if (status === 'completed') {
            // v11.3 修复：completed 节点不播放激活动画，但标记为已访问
            onNodeVisited?.(nodeId)
          }
        } else {
          console.warn('[Voice] node_enter 事件缺少 node_id:', data)
        }
      } else if (event === 'flow_end') {
        console.log('🏁 [Voice] flow_end 事件')
        // v11.3 修复：立即刷新队列并清除节点激活状态，防止残留 node_enter 重新激活
        flushAnimationQueue()
        // 确保消息的 isStreaming 标记为 false
        if (currentAssistantMsgIdRef.current) {
          setMessages(prev => prev.map(msg => 
            msg.id === currentAssistantMsgIdRef.current
              ? { ...msg, isStreaming: false }
              : msg
          ))
        }
        currentAssistantMsgIdRef.current = null
        setIsLoading(false)
        // v11.3: 清除 thinking 和 tool_progress 状态
        setThinkingStep(null)
        setToolProgress(null)
        
        // v8.2: 设备测试模式记录完整耗时
        if (mode === 'device' && sendTimeRef.current) {
          const totalTime = Date.now() - sendTimeRef.current
          setMetrics(prev => ({ ...prev, totalTime }))
          console.log('[DeviceTest] 完整耗时:', totalTime, 'ms')
        }
      } else if (event === 'thinking') {
        // v11.3: Agent Loop 推理中：{"type": "thinking", "step": 2, "node": "客服助手"}
        console.log('🧠 [Voice] thinking 事件: step=', data.step, 'node=', data.node)
        setThinkingStep({ step: data.step as number, node: data.node as string })
      } else if (event === 'tool_progress') {
        // v11.3: 工具执行中：{"type": "tool_progress", "tool": "get_weather", "description": "正在执行...", "step": 2}
        console.log('🔧 [Voice] tool_progress 事件: tool=', data.tool, 'desc=', data.description)
        setToolProgress({
          tool: data.tool as string,
          description: data.description as string,
          step: data.step as number,
        })
      } else if (event === 'silent_op') {
        // v11.3: 静默操作：{"type": "silent_op", "operation": "persist_compaction"}
        console.log('🔇 [Voice] silent_op 事件: operation=', data.operation)
      }
    },
    onError: (error) => {
      console.error('[Voice] WebSocket error:', error)
      setIsLoading(false)
      // v11.3 修复：错误时也要清除节点激活状态并刷新队列
      flushAnimationQueue()
      setThinkingStep(null)
      setToolProgress(null)
    },
    onClose: () => {
      console.log('[Voice] WebSocket closed')
    },
    // v8.1: 设备指令回调（Admin v2 Webhook 模式）
    onDeviceCommand: (command) => {
      console.log('[Voice] 收到设备指令:', command)
      // 附加到当前的 AI 消息
      if (currentAssistantMsgIdRef.current) {
        setMessages(prev => prev.map(msg =>
          msg.id === currentAssistantMsgIdRef.current
            ? { ...msg, deviceCommand: command }
            : msg
        ))
        console.log('[Voice] 设备指令已附加到当前AI消息')
      } else {
        // 缓存设备指令，等待AI消息创建时附加
        console.log('[Voice] AI消息尚未创建，缓存设备指令')
        setPendingDeviceCommand(command)
      }
    },
  })
  
  // 保持 voiceWs 的最新引用（用于避免闭包问题）
  useEffect(() => {
    voiceWsRef.current = voiceWs
  }, [voiceWs])
  
  // V6.0: VAD 自动语音检测
  // 使用 ref 存储 voiceInputMode 和 audioPlayer.isPlaying，避免闭包问题
  const voiceInputModeRef = useRef(voiceInputMode)
  useEffect(() => {
    voiceInputModeRef.current = voiceInputMode
  }, [voiceInputMode])
  
  // 🆕 发送缓存的音频数据（vad_interrupt 模式等待 cancelled 后调用）
  const flushInterruptAudioBuffer = useCallback(() => {
    const state = interruptStateRef.current
    const ws = voiceWsRef.current
    
    if (!ws?.isConnectedRef?.current) {
      console.warn('[Voice] flushInterruptAudioBuffer: WebSocket 未连接，丢弃缓存')
      state.audioBuffer = []
      return
    }
    
    // 发送缓存的每段音频
    for (const { audio, duration } of state.audioBuffer) {
      console.log(`[Voice] 发送缓存音频, 时长: ${duration}ms`)
      
      // Float32 → Int16 PCM
      const pcmInt16 = new Int16Array(audio.length)
      for (let i = 0; i < audio.length; i++) {
        const s = Math.max(-1, Math.min(1, audio[i]))
        pcmInt16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
      }
      // Int16 → Base64
      const base64Audio = btoa(
        new Uint8Array(pcmInt16.buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      )
      
      ws.sendAudio(base64Audio, 'pcm', 16000)
    }
    
    // 发送 audio_end
    ws.sendAudioEnd()
    setIsLoading(true)
    
    // 清空缓存
    state.audioBuffer = []
  }, [])
  
  // VAD 回调函数（Silero VAD 和 Energy VAD 共用）
  const vadCallbacks = {
    onSpeechStart: () => {
      const vadType = isPetMode ? 'EnergyVAD' : 'SileroVAD'
      console.log(`[${vadType}] 检测到声音开始, 模式:`, voiceInputModeRef.current, 'TTS播放中:', audioPlayer.isPlayingRef.current)
      
      const state = interruptStateRef.current
      
      // vad_interrupt 模式：如果 TTS 正在播放，执行打断
      if (voiceInputModeRef.current === 'vad_interrupt' && audioPlayer.isPlayingRef.current) {
        console.log(`[${vadType}] 🛑 打断 TTS 播放`)
        // 顺序很重要！
        // 1. 先设置打断标志，阻止新的音频数据入队
        isInterruptingRef.current = true
        // 2. 再停止播放并清空已有队列
        audioPlayer.stop()
        // 3. 设置等待 cancelled 状态
        state.isWaitingCancelled = true
        state.stopSentTime = Date.now()
        state.audioBuffer = []
        // 4. 设置 1s 超时定时器
        if (state.timeoutId) {
          clearTimeout(state.timeoutId)
        }
        state.timeoutId = setTimeout(() => {
          console.warn(`[${vadType}] ⏰ 等待 cancelled 超时（1s），强制发送缓存音频`)
          if (state.audioBuffer.length > 0) {
            console.log(`[${vadType}] 📤 超时发送缓存的音频, 共 ${state.audioBuffer.length} 段`)
            flushInterruptAudioBuffer()
          }
          state.isWaitingCancelled = false
          state.stopSentTime = null
          state.timeoutId = null
        }, 1000)  // 1s 超时
        // 5. 最后通知后端停止生成
        const ws = voiceWsRef.current
        if (ws?.isConnectedRef?.current) {
          const stopSent = ws.sendStop('User speech detected')
          console.log(`[${vadType}] 🛑 sendStop 发送结果:`, stopSent, '等待 cancelled...')
        } else {
          console.warn(`[${vadType}] 🛑 sendStop 失败，WebSocket 未连接`)
          // 发送失败，重置等待状态
          state.isWaitingCancelled = false
          if (state.timeoutId) {
            clearTimeout(state.timeoutId)
            state.timeoutId = null
          }
        }
      }
      
      setIsRecording(true)
      // 使用 ref 获取最新的 voiceWs
      const ws = voiceWsRef.current
      if (ws?.isConnectedRef?.current) {
        ws.sendAudioStart()
      } else {
        console.warn(`[${vadType}] sendAudioStart 失败，WebSocket 未连接`)
      }
    },
    onSpeechEnd: async (audio: Float32Array, duration: number) => {
      const vadType = isPetMode ? 'EnergyVAD' : 'SileroVAD'
      console.log(`[${vadType}] 检测到声音结束, 时长:`, duration, 'ms')
      setIsRecording(false)
      
      if (duration < 500) {
        console.log(`[${vadType}] 声音太短，忽略`)
        return
      }
      
      const state = interruptStateRef.current
      
      // 🆕 如果正在等待 cancelled，缓存音频而不是直接发送
      if (state.isWaitingCancelled) {
        console.log(`[${vadType}] 📦 缓存音频（等待 cancelled）, 时长:`, duration, 'ms')
        // 复制音频数据（因为原数据可能被复用）
        const audioCopy = new Float32Array(audio)
        state.audioBuffer.push({ audio: audioCopy, duration })
        return
      }
      
      // 正常发送音频
      try {
        // Float32 → Int16 PCM
        const pcmInt16 = new Int16Array(audio.length)
        for (let i = 0; i < audio.length; i++) {
          const s = Math.max(-1, Math.min(1, audio[i]))
          pcmInt16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
        }
        // Int16 → Base64
        const base64Audio = btoa(
          new Uint8Array(pcmInt16.buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
        )
        
        // 使用 ref 获取最新的 voiceWs
        const ws = voiceWsRef.current
        if (ws?.isConnectedRef?.current) {
          ws.sendAudio(base64Audio, 'pcm', 16000)  // 发送纯 PCM 格式
          ws.sendAudioEnd()
          setIsLoading(true)
          
          // vad 不可打断模式：发送音频后立即暂停 VAD，防止在等待 TTS 响应期间再次触发
          // VAD 会在 TTS 播放结束后（onPlayEnd）恢复
          if (voiceInputModeRef.current === 'vad') {
            vadRef.current?.pause()
            console.log(`[${vadType}] ⏸️ vad 模式：发送音频后暂停，等待 TTS`)
          }
        } else {
          console.error(`[${vadType}] WebSocket 未连接，无法发送音频`)
        }
      } catch (err) {
        console.error(`[${vadType}] 音频转换失败:`, err)
      }
    },
    onError: (error: string) => {
      const vadType = isPetMode ? 'EnergyVAD' : 'SileroVAD'
      console.error(`[${vadType}] 错误:`, error)
    },
  }
  
  // Silero VAD（用于人声检测）
  const sileroVad = useVAD({
    // vad_interrupt 模式使用更高的灵敏度阈值
    positiveSpeechThreshold: voiceInputMode === 'vad_interrupt' ? vadSensitivity : 0.5,
    // 静音后延迟时间，500ms 避免用户说话中间停顿被误判为结束
    redemptionMs: 500,
    ...vadCallbacks,
  })
  
  // Energy VAD（用于 Pet ASR 等非人声检测）
  const energyVad = useEnergyVAD({
    // Pet 模式使用较低的能量阈值，更容易检测到声音
    energyThreshold: 0.008,
    silenceTimeoutMs: 1000,  // Pet 声音可能有较长间隔
    minSpeechMs: 300,
    ...vadCallbacks,
  })
  
  // 根据 asrProvider 选择使用哪个 VAD
  const vad = isPetMode ? energyVad : sileroVad
  
  // VAD ref（用于在回调中访问最新的 vad 实例）
  const vadRef = useRef(vad)
  useEffect(() => {
    vadRef.current = vad
  }, [vad])
  
  // Device ID 历史记录（按项目区分存储到 localStorage）
  const DEVICE_ID_HISTORY_KEY = `axiom_device_id_history_${projectId}`
  const MAX_HISTORY_COUNT = 10
  
  const [deviceIdHistory, setDeviceIdHistory] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(DEVICE_ID_HISTORY_KEY)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  })
  
  // 保存历史到 localStorage
  const saveDeviceIdToHistory = useCallback((id: string) => {
    if (!id.trim()) return
    setDeviceIdHistory(prev => {
      // 如果已存在，移到最前面
      const filtered = prev.filter(item => item !== id)
      const newHistory = [id, ...filtered].slice(0, MAX_HISTORY_COUNT)
      localStorage.setItem(DEVICE_ID_HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
  }, [DEVICE_ID_HISTORY_KEY])
  
  // 从历史中删除
  const removeDeviceIdFromHistory = useCallback((id: string) => {
    setDeviceIdHistory(prev => {
      const newHistory = prev.filter(item => item !== id)
      localStorage.setItem(DEVICE_ID_HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
  }, [DEVICE_ID_HISTORY_KEY])
  
  // ====== 动画队列机制 ======
  // 将流程事件放入队列，按固定间隔播放，确保动画效果可见
  const animationQueueRef = useRef<AnimationEvent[]>([])
  const animationTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isAnimatingRef = useRef(false)
  
  // 动画调度器：从队列中取出事件并执行
  const processAnimationQueue = useCallback(() => {
    if (animationQueueRef.current.length === 0) {
      // 队列为空，停止调度
      isAnimatingRef.current = false
      if (animationTimerRef.current) {
        clearInterval(animationTimerRef.current)
        animationTimerRef.current = null
      }
      console.log('🎬 [Animation] 队列为空，停止动画调度')
      return
    }
    
    // 取出队列中的第一个事件
    const event = animationQueueRef.current.shift()
    if (!event) return
    
    console.log('🎬 [Animation] 处理动画事件:', JSON.stringify({
      type: event.type,
      nodeId: event.nodeId,
      queueLength: animationQueueRef.current.length,
    }))
    
    switch (event.type) {
      case 'flow_start':
        // 流程开始：激活入口节点
        onNodeActivate?.('entry')
        onNodeVisited?.('entry')
        break
        
      case 'node_enter':
        if (event.nodeId) {
          // 先激活节点（显示虚线动画）
          onNodeActivate?.(event.nodeId)
          // 延迟后标记为已访问（虚线变实线）
          setTimeout(() => {
            onNodeVisited?.(event.nodeId!)
          }, ANIMATION_INTERVAL * 0.8) // 在下一个事件之前完成
        }
        break
        
      case 'flow_end':
        // 流程结束：清除活动节点
        onNodeActivate?.(null)
        break
    }
  }, [onNodeActivate, onNodeVisited])
  
  // 启动动画调度器
  const startAnimationScheduler = useCallback(() => {
    if (isAnimatingRef.current) return
    
    isAnimatingRef.current = true
    console.log('🎬 [Animation] 启动动画调度器')
    
    // 立即处理第一个事件
    processAnimationQueue()
    
    // 设置定时器处理后续事件
    animationTimerRef.current = setInterval(() => {
      processAnimationQueue()
    }, ANIMATION_INTERVAL)
  }, [processAnimationQueue])
  
  // 将事件添加到动画队列
  const enqueueAnimationEvent = useCallback((event: AnimationEvent) => {
    console.log('🎬 [Animation] 入队动画事件:', JSON.stringify({
      type: event.type,
      nodeId: event.nodeId,
    }))
    animationQueueRef.current.push(event)
    
    // 如果调度器未运行，启动它
    if (!isAnimatingRef.current) {
      startAnimationScheduler()
    }
  }, [startAnimationScheduler])
  
  // v11.3 新增：立即刷新并清空动画队列
  // 当 flow_end 或异常发生时调用，防止残留的 node_enter 事件在延迟后重新激活节点
  const flushAnimationQueue = useCallback(() => {
    console.log('🎬 [Animation] 刷新并清空队列，剩余事件:', animationQueueRef.current.length)
    
    // 将队列中所有 node_enter 的节点直接标记为已访问（跳过动画延迟）
    for (const evt of animationQueueRef.current) {
      if (evt.type === 'node_enter' && evt.nodeId) {
        onNodeVisited?.(evt.nodeId)
      }
    }
    
    // 清空队列
    animationQueueRef.current = []
    
    // 停止调度器
    if (animationTimerRef.current) {
      clearInterval(animationTimerRef.current)
      animationTimerRef.current = null
    }
    isAnimatingRef.current = false
    
    // 清除活动节点
    onNodeActivate?.(null)
  }, [onNodeActivate, onNodeVisited])
  
  // 组件卸载时清理
  useEffect(() => {
    return () => {
      // 清理动画计时器
      if (animationTimerRef.current) {
        clearInterval(animationTimerRef.current)
        animationTimerRef.current = null
      }
      animationQueueRef.current = []
      isAnimatingRef.current = false
      
      // V6.0: 清理录音资源
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
      audioChunksRef.current = []
      
      // 🔧 修复：清理 VAD 资源（关闭麦克风）
      // 注意：这里不能直接调用 vad.stop()，因为 vad 在闭包中可能过时
      // 使用 vadRef 来获取最新的 vad 实例
      if (vadRef.current?.isListening) {
        vadRef.current.stop()
        console.log('[Voice] 组件卸载，停止 VAD 监听')
      }
    }
  }, [])

  // 发送消息（使用 Flow API）
  // 注意：打开测试面板时已自动保存，这里不再检查 isSaved
  const handleSend = async (userInput: string) => {
    if (!userInput.trim() || isLoading) return

    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: "user",
      content: userInput.trim(),
      timestamp: new Date(),
    }

    setMessages(prev => [...prev, userMessage])
    const message = userInput.trim()
    setIsLoading(true)
    setStreamingContent("")
    setExecutionPath([])
    setCurrentNode("")
    setRouteInfo(null)
    
    // v8.2: 设备测试模式记录发送时间
    if (mode === 'device') {
      sendTimeRef.current = Date.now()
      firstTokenTimeRef.current = 0
      setMetrics({})
    }

    // 重置流程图执行状态
    onExecutionReset?.()

    // 清理动画队列（新消息开始，重置动画状态）
    if (animationTimerRef.current) {
      clearInterval(animationTimerRef.current)
      animationTimerRef.current = null
    }
    animationQueueRef.current = []
    isAnimatingRef.current = false

    // v11.3: v2 模式下文本消息也使用 WebSocket（支持 identity_id）
    const currentApiVersion = mode === 'device' ? 'v2' : apiVersion
    if (currentApiVersion === 'v2') {
      console.log('[Text] v2 模式，准备通过 WebSocket 发送文字消息:', message)
      
      const ws = voiceWsRef.current
      if (ws?.isConnectedRef?.current) {
        // WebSocket 已连接，直接发送
        console.log('[Text] WebSocket 已连接，直接发送文字消息')
        const success = ws.sendMessage(message)
        if (success) {
          console.log('[Text] v2 文字消息已发送，等待服务器响应...')
          return
        } else {
          console.error('[Text] WebSocket 发送失败，降级到 SSE')
        }
      } else {
        // WebSocket 未连接，主动建立连接
        console.log('[Text] WebSocket 未连接，主动建立连接以发送文字消息')
        
        try {
          // 主动连接 WebSocket
          if (!ws) {
            console.error('[Text] WebSocket 实例不存在，降级到 SSE')
            throw new Error('WebSocket instance not available')
          }
          ws.connect()
          
          // 等待连接建立（最多等待 3 秒）
          let retryCount = 0
          const maxRetries = 30 // 30 * 100ms = 3秒
          
          const waitForConnection = async (): Promise<boolean> => {
            return new Promise((resolve) => {
              const checkConnection = () => {
                if (ws?.isConnectedRef?.current) {
                  resolve(true)
                } else if (retryCount >= maxRetries) {
                  resolve(false)
                } else {
                  retryCount++
                  setTimeout(checkConnection, 100)
                }
              }
              checkConnection()
            })
          }
          
          const connected = await waitForConnection()
          if (connected && ws) {
            console.log('[Text] WebSocket 连接成功，发送文字消息')
            const success = ws.sendMessage(message)
            if (success) {
              console.log('[Text] v2 文字消息已发送，等待服务器响应...')
              return
            }
          } else {
            console.error('[Text] WebSocket 连接超时，降级到 SSE')
          }
        } catch (error) {
          console.error('[Text] WebSocket 连接失败，降级到 SSE:', error)
        }
      }
    }

    try {
      // 构建历史消息
      const history = messages.map(m => ({
        role: m.role,
        content: m.content,
      }))

      // 注意：不再构建 flowConfig，后端从数据库读取已保存的配置
      // 这样可以确保测试使用的是已保存的配置，避免前端缓存与后端不一致

      // 获取Token并添加到请求头
      const token = TokenManager.getToken()
      console.log('🔍 [Flow Execute] Token check:', {
        hasToken: !!token,
        tokenLength: token?.length,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'null',
        localStorageKeys: Object.keys(localStorage).filter(k => k.includes('token') || k.includes('auth')),
      })
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      
      // 添加Authorization header
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
        console.log('✅ [Flow Execute] Authorization header added')
      } else {
        console.error('❌ [Flow Execute] No token found! Request will fail with 401')
        console.error('Available localStorage keys:', Object.keys(localStorage))
        throw new Error('未找到认证Token，请重新登录')
      }

      console.log('📤 [Flow Execute] Sending request:', {
        url: `${API_BASE}/chat/stream`,
        hasAuthHeader: !!headers['Authorization'],
        headers: Object.keys(headers),
        sessionId: sessionId,  // 用于 Memory 功能
      })

      // 不再传递 flow 参数，后端从数据库读取最新配置
      // 这样可以确保测试使用的是已保存的配置，避免前端缓存与后端不一致
      // session_id 用于 Memory 功能：启用记忆时，消息会存储到对应的 session 中
      // device_id 用于 IoT 设备控制场景（可选）
      const requestBody: Record<string, unknown> = {
        message: message,
        history: history,
        project_id: projectId,
        session_id: sessionId,  // Memory 功能所需，由前端生成 UUID v4
      }
      // 只有在 deviceId 有值时才添加到请求中，并保存到历史
      if (deviceId.trim()) {
        requestBody.device_id = deviceId.trim()
        saveDeviceIdToHistory(deviceId.trim())
      }

      // v10.0 新增：identity_id 用于身份模拟场景
      if (identityId.trim()) {
        requestBody.identity_id = identityId.trim()
      }
      
      const response = await fetch(`${API_BASE}/chat/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      })

      console.log('📥 [Flow Execute] Response status:', response.status, response.statusText)

      if (!response.ok) {
        const errorText = await response.text().catch(() => '无法读取错误信息')
        console.error('❌ [Flow Execute] Request failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText,
          headers: Object.fromEntries(response.headers.entries()),
        })
        
        // 尝试解析错误响应体（统一响应格式）
        let errorMessage = `请求失败 (${response.status})`
        try {
          const errorData = JSON.parse(errorText)
          if (errorData.error && errorData.error.message) {
            // 使用后端返回的具体错误消息
            errorMessage = errorData.error.message
            // 如果是项目不存在，提供更友好的提示
            if (errorData.error.code === 'PROJ_002') {
              errorMessage = `项目不存在（项目ID: ${projectId}）。请先点击"保存"按钮保存项目配置，然后再发送消息。`
            }
          }
        } catch (e) {
          // 如果解析失败，使用状态码判断
          if (response.status === 401) {
            errorMessage = '认证失败，请重新登录'
          } else if (response.status === 404) {
            // 404可能是路由不存在或资源不存在，尝试从响应体判断
            if (errorText.includes('PROJ_002') || errorText.includes('项目不存在') || errorText.includes('Project not found')) {
              errorMessage = `项目不存在（项目ID: ${projectId}）。请先点击"保存"按钮保存项目配置，然后再发送消息。`
            } else {
              errorMessage = 'API端点不存在，请检查后端服务配置'
            }
          } else if (response.status === 500) {
            errorMessage = '服务器内部错误，请查看后端日志'
          } else if (response.status === 0 || response.status === 503) {
            errorMessage = '无法连接到后端服务，请确认Agent Admin Service已启动（端口8001）'
          }
        }
        
        throw new Error(errorMessage)
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      let fullContent = ""
      let fullReasoning = ""             // v13.0: 当前步骤推理内容（tool_call 时归零）
      let intermediateReasoning = ""    // v13.0: 步骤 1~N-1 的中间推理（历史消息灰色块）
      let lastReasoningStep: number | null = null  // v13.0: 跟踪步骤切换用于加换行

      console.log('📥 [Flow Execute] Starting SSE stream reading...', {
        hasReader: !!reader,
        hasBody: !!response.body,
      })

      if (reader) {
        let streamDone = false  // 🔧 修复：添加标志用于跳出外层循环
        let buffer = ""  // 🔧 新增：缓冲区，处理跨chunk的SSE事件
        let messageAdded = false  // 🔧 新增：防止重复添加消息的标志
        let lastToolCallTime: number | null = null  // 🔧 新增：记录最后一次工具调用时间
        
        while (true) {
          const { done, value } = await reader.read()
          console.log('📥 [Flow Execute] SSE chunk:', { done, hasValue: !!value, valueLength: value?.length })
          if (done || streamDone) {
            console.log('📥 [Flow Execute] SSE stream ended, fullContent length:', fullContent.length, 'fullReasoning length:', fullReasoning.length)
            // 物理流结束（后端未发 [DONE]），同样需要构建消息
            if (!messageAdded && (fullContent || fullReasoning)) {
              const finalContent = fullContent || fullReasoning
              // 灰色推理块：中间步骤推理（有 content 时）；无 content 时推理提升为主内容，不单独存
              const finalReasoning = fullContent ? intermediateReasoning : ''
              if (!fullContent && fullReasoning) {
                setStreamingContent(fullReasoning)
              }
              const aiMessage: ChatMessage = {
                id: `msg-${Date.now()}`,
                role: 'assistant',
                content: finalContent,
                timestamp: new Date(),
                ...(finalReasoning ? { reasoning: finalReasoning } : {}),
              }
              console.log('➕ [Flow Execute] 物理流结束，添加AI消息:', {
                contentLength: aiMessage.content.length,
                hasReasoning: !!finalReasoning,
                reasoningPromoted: !fullContent && !!fullReasoning,
              })
              setMessages(prev => [...prev, aiMessage])
              messageAdded = true
            }
            setIsLoading(false)
            setReasoningContent('')
            break
          }

          const chunk = decoder.decode(value)
          const timestamp = new Date().toISOString()
          console.log(`📥 [Flow Execute] Decoded chunk at ${timestamp}:`, chunk)
          
          // 🔧 修复：将新chunk添加到缓冲区
          buffer += chunk
          
          // 🔧 强化修复：处理后端SSE格式错误的问题
          // 后端可能发送: data: {...}data: {...} 而不是标准的 data: {...}\n\ndata: {...}
          // 首先尝试标准分割
          let events = buffer.split('\n\n')
          
          // 如果只有一个事件且包含多个data:，说明后端格式有问题
          if (events.length === 1 && buffer.includes('data:') && buffer.split('data:').length > 2) {
            console.warn('🚨 [Flow Execute] 检测到后端SSE格式错误，尝试修复...')
            // 按 'data:' 分割，然后重新构造
            const dataParts = buffer.split('data:').filter(part => part.trim())
            events = dataParts.map(part => 'data:' + part.trim())
            buffer = '' // 清空buffer，因为我们已经处理了所有数据
            console.log('🔧 [Flow Execute] 修复后的事件数量:', events.length)
          } else {
            // 标准处理：保留最后一个可能不完整的事件
            buffer = events.pop() || ''
          }
          
          console.log('📦 [Flow Execute] SSE events split:', {
            eventsCount: events.length,
            bufferRemaining: buffer.length,
            events: events.map(e => e.substring(0, 100) + (e.length > 100 ? '...' : ''))
          })
          
          for (const event of events) {
            if (!event.trim()) continue
            
            const lines = event.split('\n')
            console.log('📥 [Flow Execute] Processing SSE event lines:', lines)
            
            for (const line of lines) {
              console.log('📥 [Flow Execute] Processing line:', line)
              if (line.startsWith('data: ')) {
                const data = line.slice(6).trim()
                const timestamp = new Date().toISOString()
                console.log(`📥 [Flow Execute] SSE data at ${timestamp}:`, data)
              
              if (data === '[DONE]') {
                console.log('🏁 [Flow Execute] 收到 [DONE] 信号，准备添加AI消息')
                console.log('🏁 [Flow Execute] fullContent 内容:', fullContent)
                console.log('🏁 [Flow Execute] fullContent 长度:', fullContent.length)
                
                // 🔧 修复：防止重复添加消息
                if (!messageAdded) {
                  await new Promise(resolve => setTimeout(resolve, 100))
                  // v13.0: 若无 content 但有 reasoning，说明推理本身就是最终回复
                  const finalContent = fullContent || fullReasoning
                  // 灰色推理块：只存中间步骤推理；推理提升为主内容时不单独存推理块
                  const finalReasoning = fullContent ? intermediateReasoning : ''
                  if (!fullContent && fullReasoning) {
                    // 推理提升为主内容时，同步更新打字机显示
                    setStreamingContent(fullReasoning)
                  }
                  const aiMessage: ChatMessage = {
                    id: `msg-${Date.now()}`,
                    role: "assistant",
                    content: finalContent,
                    timestamp: new Date(),
                    ...(finalReasoning ? { reasoning: finalReasoning } : {}),
                  }
                  console.log('➕ [Flow Execute] 添加AI消息 (位置: [DONE]):', JSON.stringify({
                    id: aiMessage.id,
                    contentLength: aiMessage.content.length,
                    contentPreview: aiMessage.content.substring(0, 100),
                    hasReasoning: !!finalReasoning,
                    reasoningPromoted: !fullContent && !!fullReasoning,
                  }))
                  setMessages(prev => {
                    console.log('➕ [Flow Execute] setMessages 回调 (位置: [DONE]), 当前消息数:', prev.length)
                    return [...prev, aiMessage]
                  })
                  messageAdded = true  // 🔧 修复：标记消息已添加
                } else {
                  console.log('⚠️ [Flow Execute] 重复的 [DONE] 信号，跳过添加消息')
                }
                
                setIsLoading(false)  // 🔧 修复：确保在流结束时设置Loading状态
                setReasoningContent('')  // v13.0: 清除推理内容
                streamDone = true  // 🔧 修复：设置标志，让外层循环也退出
                
                // 🔧 不立即清空streamingContent，让打字机自然完成
                // setStreamingContent("") // 注释掉立即清空
                break
              }

              try {
                const parsed = JSON.parse(data)
                console.log('📥 [Flow Execute] 解析后的数据:', JSON.stringify(parsed))
                
                // 处理不同类型的事件
                // 后端可能返回两种格式：
                // 1. {"type": "xxx", ...} - 带类型的事件
                // 2. {"content": "xxx"} - 直接的内容块
                
                // 如果没有 type 字段但有 content 字段，说明是内容块
                if (!parsed.type && parsed.content !== undefined) {
                  const contentTime = new Date().toISOString()
                  const contentTimestamp = Date.now()
                  
                  // 🔧 分析工具调用后的内容延迟
                  if (lastToolCallTime) {
                    const delayAfterTool = contentTimestamp - lastToolCallTime
                    console.log(`⏱️ [Timing] 工具调用后 ${delayAfterTool}ms 收到无type内容:`, parsed.content.substring(0, 50))
                  }
                  
                  console.log(`📝 [Flow Execute] 无type字段的内容块 at ${contentTime}:`, parsed.content)
                  fullContent += parsed.content
                  setStreamingContent(fullContent)
                  // v11.3: 收到内容时清除思考状态，但保持工具调用状态更长时间
                  setThinkingStep(null)
                  // 🔧 延迟清除工具调用状态，避免"一闪就没了"的问题
                  if (toolProgress) {
                    setTimeout(() => setToolProgress(null), 1500) // 1.5秒后清除
                  }
                  console.log('📝 [Flow Execute] 累积内容长度:', fullContent.length)
                } else {
                  console.log('📥 [Flow Execute] 事件类型:', parsed.type)
                  switch (parsed.type) {
                    case 'start':
                      console.log('🚀 [Flow Execute] Stream started')
                      break
                    case 'flow_start':
                      console.log('🎯 [SSE] flow_start 事件，激活入口节点')
                      setCurrentNode('entry')
                      // 动画事件入队（解耦内容显示与动画）
                      enqueueAnimationEvent({ type: 'flow_start', timestamp: Date.now() })
                      break
                    case 'node_enter':
                      // v13.0 Binder: 检查是否来自 Binder 分支
                      if (parsed._from_binder) {
                        console.log('🎯 [Binder] node_enter from branch', parsed._branch_index,
                          'silent=', parsed._silent, ':', JSON.stringify({
                          node: parsed.node,
                          name: parsed.name,
                          status: parsed.status,
                        }))

                        // 所有分支都显示在执行路径，但要标记分支信息
                        setCurrentNode(parsed.node)
                        let isNewNode = false
                        setExecutionPath(prev => {
                          const existingIndex = prev.findIndex(item => item.node === parsed.node)
                          if (existingIndex >= 0) {
                            const updated = [...prev]
                            updated[existingIndex] = {
                              node: parsed.node,
                              name: parsed.name,
                              status: parsed.status,
                              model: parsed.model,
                              // v13.0 Binder: 分支信息（用于视觉区分）
                              _branch_index: parsed._branch_index,
                              _is_primary: !parsed._silent,  // 主分支标记
                              _from_binder: true,
                              _silent: parsed._silent,
                            }
                            return updated
                          }
                          isNewNode = true
                          return [...prev, {
                            node: parsed.node,
                            name: parsed.name,
                            status: parsed.status,
                            model: parsed.model,
                            // v13.0 Binder: 分支信息（用于视觉区分）
                            _branch_index: parsed._branch_index,
                            _is_primary: !parsed._silent,  // 主分支标记
                            _from_binder: true,
                            _silent: parsed._silent,
                          }]
                        })

                        // 播放节点动画
                        if (parsed.status === 'running') {
                          enqueueAnimationEvent({ type: 'node_enter', nodeId: parsed.node, timestamp: Date.now() })
                        } else if (parsed.status === 'completed') {
                          onNodeVisited?.(parsed.node)
                        } else if (isNewNode) {
                          onNodeVisited?.(parsed.node)
                        }
                      } else {
                        // 普通 node_enter 事件（非 Binder）
                        console.log('🎯 [SSE] node_enter 事件:', JSON.stringify({
                          node: parsed.node,
                          name: parsed.name,
                          status: parsed.status,
                          model: parsed.model,
                        }))
                        setCurrentNode(parsed.node)
                        // 修复：检查节点是否已存在，如果存在则更新状态，否则添加
                        // 因为 AgentNode 会发射两次 node_enter（running + completed）
                        let isNewNode = false
                        setExecutionPath(prev => {
                          const existingIndex = prev.findIndex(item => item.node === parsed.node)
                          if (existingIndex >= 0) {
                            // 节点已存在，更新状态
                            const updated = [...prev]
                            updated[existingIndex] = {
                              node: parsed.node,
                              name: parsed.name,
                              status: parsed.status,
                              model: parsed.model
                            }
                            return updated
                          }
                          // 新节点，添加到路径
                          isNewNode = true
                          return [...prev, { node: parsed.node, name: parsed.name, status: parsed.status, model: parsed.model }]
                        })
                        // 🔧 修复动画混乱：只在 status=running 时激活动画（播放脉冲效果）
                        // status=completed 的事件不入队动画，避免重复激活同一节点
                        if (parsed.status === 'running') {
                          enqueueAnimationEvent({ type: 'node_enter', nodeId: parsed.node, timestamp: Date.now() })
                        } else if (parsed.status === 'completed') {
                          // v11.3 修复：status=completed 的节点虽不播放激活动画，
                          // 但仍需标记为已访问，否则指向该节点的边无法显示 completed 样式
                          onNodeVisited?.(parsed.node)
                        } else if (isNewNode) {
                          // 修复：路由节点等中间节点可能不会发送 status=running 或 completed
                          // 但仍需要被标记为已访问以支持边动画
                          // 只在第一次收到该节点的 node_enter 事件时标记为已访问
                          onNodeVisited?.(parsed.node)
                        }
                      }
                      break
                    case 'route_result':
                      console.log('🔀 [SSE] route_result 事件:', JSON.stringify({
                        condition: parsed.condition,
                        target: parsed.target,
                        reason: parsed.reason,
                      }))
                      setRouteInfo({ 
                        condition: parsed.condition, 
                        target: parsed.target, 
                        reason: parsed.reason || '' 
                      })
                      break
                    case 'content':
                      // v13.0 Binder: 检查是否来自 Binder 分支
                      if (parsed._from_binder) {
                        const contentTime = new Date().toISOString()
                        const contentTimestamp = Date.now()
                        
                        // 🔧 分析工具调用后的内容延迟
                        if (lastToolCallTime && !parsed._silent) {
                          const delayAfterTool = contentTimestamp - lastToolCallTime
                          console.log(`⏱️ [Timing] 工具调用后 ${delayAfterTool}ms 收到内容:`, parsed.content.substring(0, 50))
                        }
                        
                        console.log(`📝 [Binder] type=content from branch ${parsed._branch_index} at ${contentTime},`,
                          'silent=', parsed._silent, 'content:', parsed.content)

                        // 只累积非静默分支的内容
                        if (!parsed._silent) {
                          fullContent += parsed.content
                          setStreamingContent(fullContent)
                          // v11.3: 收到内容时清除思考状态，但保持工具调用状态更长时间
                          setThinkingStep(null)
                          // 🔧 延迟清除工具调用状态，避免"一闪就没了"的问题
                          if (toolProgress) {
                            setTimeout(() => setToolProgress(null), 1500) // 1.5秒后清除
                          }
                          console.log('📝 [Binder] 累积主分支内容长度:', fullContent.length)
                        } else {
                          console.log('🔇 [Binder] 跳过静默分支内容 (branch', parsed._branch_index, ')')
                        }
                      } else {
                        // 普通 content 事件（非 Binder）
                        const contentTime = new Date().toISOString()
                        const contentTimestamp = Date.now()
                        
                        // 🔧 分析工具调用后的内容延迟
                        if (lastToolCallTime) {
                          const delayAfterTool = contentTimestamp - lastToolCallTime
                          console.log(`⏱️ [Timing] 工具调用后 ${delayAfterTool}ms 收到内容:`, parsed.content.substring(0, 50))
                        }
                        
                        console.log(`📝 [Flow Execute] type=content at ${contentTime}:`, parsed.content)
                        fullContent += parsed.content
                        setStreamingContent(fullContent)
                        // v11.3: 收到内容时清除思考状态，但保持工具调用状态更长时间
                        setThinkingStep(null)
                        // 🔧 延迟清除工具调用状态，避免"一闪就没了"的问题
                        if (toolProgress) {
                          setTimeout(() => setToolProgress(null), 1500) // 1.5秒后清除
                        }
                        console.log('📝 [Flow Execute] 累积内容长度:', fullContent.length)
                      }
                      break
                    case 'done':
                      console.log('✅ [Flow Execute] 收到 type=done 事件')
                      break
                    case 'flow_end':
                      console.log('🏁 [SSE] flow_end 事件:', JSON.stringify({ path: parsed.path }))
                      // v11.3 修复：flow_end 意味着流程已完成，所有节点都应标记为 completed
                      // 后端 path 中的 status 是节点进入时的快照（通常是 "running"），不可靠。
                      // 且某些节点（如 entry）永远不会收到 node_enter(status=completed)。
                      // 策略：用后端 path 确保节点顺序完整，但强制所有节点 status → completed
                      if (parsed.path && Array.isArray(parsed.path)) {
                        setExecutionPath(parsed.path.map((item: ExecutionNode) => ({
                          ...item,
                          status: 'completed',
                        })))
                      } else {
                        // 没有 path 时，直接把本地所有 running 的标记为 completed
                        setExecutionPath(prev => prev.map(item => ({
                          ...item,
                          status: 'completed',
                        })))
                      }
                      // v11.3 修复：立即刷新队列并清除节点激活状态，防止残留 node_enter 重新激活
                      flushAnimationQueue()
                      // v11.3: 流结束时强制清除所有状态
                      setThinkingStep(null)
                      setToolProgress(null)
                      // v13.0: reasoning 内容已写入消息，清除流式状态
                      setReasoningContent('')
                      break
                    // v11.3 新增：Structured Agent 可观测性事件
                    case 'thinking':
                      // Agent Loop 推理中：{"type": "thinking", "step": 2, "node": "客服助手"}
                      console.log('🧠 [SSE] thinking 事件: step=', parsed.step, 'node=', parsed.node)
                      setThinkingStep({ step: parsed.step, node: parsed.node })
                      break
                    case 'reasoning':
                      // v13.0 中间推理内容：{"type": "reasoning", "content": "...", "step": 1, "node": "助手"}
                      console.log('💭 [SSE] reasoning 事件: step=', parsed.step, 'node=', parsed.node)
                      // 只在步骤切换时加换行，不在每个 token 之间加
                      if (fullReasoning && parsed.step != null && parsed.step !== lastReasoningStep) {
                        fullReasoning += '\n\n'
                      }
                      lastReasoningStep = parsed.step ?? null
                      fullReasoning += (parsed.content || '')
                      setReasoningContent(fullReasoning)
                      setThinkingStep({ step: parsed.step ?? 1, node: parsed.node ?? '' })
                      break
                    case 'reasoning_complete':
                      // v13.0 推理完成标记：{"type": "reasoning_complete", "is_final": true/false}
                      console.log('✅ [SSE] reasoning_complete 事件: is_final=', parsed.is_final)
                      if (parsed.is_final) {
                        // is_final=true：本次推理就是最终回复，将 reasoning 提升为主内容
                        fullContent = fullReasoning
                        fullReasoning = ''
                        lastReasoningStep = null
                        setStreamingContent(fullContent)
                        setReasoningContent('')
                      }
                      // is_final=false：中间步骤推理结束，保留 reasoning，等待后续 content
                      break
                    case 'tool_progress':
                      // 工具执行中：{"type": "tool_progress", "tool": "get_weather", "description": "正在执行...", "step": 2}
                      console.log('🔧 [SSE] tool_progress 事件: tool=', parsed.tool, 'desc=', parsed.description, 'step=', parsed.step)
                      setToolProgress({
                        tool: parsed.tool,
                        description: parsed.description,
                        step: parsed.step,
                      })
                      break
                    case 'tool_call':
                      // 工具调用：{"type": "tool_call", "tools": [{"tool_id": "xxx", "tool_name": "play_audio", "arguments": {...}}]}
                      const toolCallTime = new Date().toISOString()
                      const toolCallTimestamp = Date.now()
                      lastToolCallTime = toolCallTimestamp
                      console.log(`🔧 [SSE] tool_call 事件 at ${toolCallTime}:`, JSON.stringify(parsed.tools || []))
                      // v13.0: tool_call 标志当前推理步骤结束，归档中间推理并重置给下一步骤
                      if (fullReasoning) {
                        intermediateReasoning += (intermediateReasoning ? '\n\n' : '') + fullReasoning
                        fullReasoning = ''
                        lastReasoningStep = null
                      }
                      
                      // 🎯 显示工具调用状态，但不影响内容流
                      if (parsed.tools && parsed.tools.length > 0) {
                        const tool = parsed.tools[0] // 显示第一个工具
                        const toolName = tool.tool_name || 'unknown'
                        const toolArgs = tool.arguments || {}
                        
                        // 根据工具类型生成友好的描述
                        let description = `正在执行 ${toolName}`
                        if (toolName === 'play_audio' && toolArgs.audio_id) {
                          description = '🎵 正在播放音频...'
                        } else if (toolName.includes('search')) {
                          description = '🔍 正在搜索...'
                        } else if (toolName.includes('query')) {
                          description = '📋 正在查询...'
                        }
                        
                        // 🔧 优化：设置工具状态时清除思考状态，避免冲突
                        setThinkingStep(null)
                        setToolProgress({
                          tool: toolName,
                          description: description,
                          step: 1
                        })
                        
                        // 🔧 状态会在收到 content 事件或流结束时被统一清除
                      }
                      break
                    case 'silent_op':
                      // 静默操作：{"type": "silent_op", "operation": "persist_compaction"}
                      console.log('🔇 [SSE] silent_op 事件: operation=', parsed.operation)
                      // 静默操作不需要 UI 展示，仅日志
                      break
                    case 'error':
                      // 处理后端返回的错误
                      console.error('❌ [Flow Execute] Backend error:', parsed.error)
                      const backendErrorMessage: ChatMessage = {
                        id: `msg-${Date.now()}`,
                        role: "assistant",
                        content: `❌ 后端错误: ${parsed.error}`,
                        timestamp: new Date(),
                      }
                      console.log('➕ [Flow Execute] 添加错误消息 (位置: error):', JSON.stringify({
                        id: backendErrorMessage.id,
                        content: backendErrorMessage.content,
                      }))
                      setMessages(prev => {
                        console.log('➕ [Flow Execute] setMessages 回调 (位置: error), 当前消息数:', prev.length)
                        return [...prev, backendErrorMessage]
                      })
                      setStreamingContent("")
                      setReasoningContent('')  // v13.0: 清除推理内容
                      setIsLoading(false)
                      return
                    default:
                      console.log('⚠️ [Flow Execute] 未知事件类型:', parsed.type)
                  }
                }
                
                if (parsed.error) throw new Error(parsed.error)
              } catch (e) {
                console.error('⚠️ [Flow Execute] JSON parse error:', e, 'data:', data)
                // 🔧 修复：检查是否是多个JSON连接的问题
                if (data.includes('}{')) {
                  console.error('🚨 [Flow Execute] 检测到多个JSON连接，这是SSE解析问题！原始数据:', data)
                  // 尝试分割并单独解析每个JSON
                  const jsonParts = data.split('}{')
                  for (let i = 0; i < jsonParts.length; i++) {
                    let jsonStr = jsonParts[i]
                    if (i > 0) jsonStr = '{' + jsonStr
                    if (i < jsonParts.length - 1) jsonStr = jsonStr + '}'
                    
                    try {
                      const recoveredParsed = JSON.parse(jsonStr)
                      console.log('🔧 [Flow Execute] 修复解析成功:', JSON.stringify(recoveredParsed))
                      
                      // 🔧 重要：对恢复的 JSON 数据执行相同的处理逻辑
                      if (recoveredParsed.type === 'content') {
                        // v13.0 Binder: 检查是否来自 Binder 分支
                        if (recoveredParsed._from_binder) {
                          console.log('📝 [Binder Recovery] type=content from branch', recoveredParsed._branch_index,
                            'silent=', recoveredParsed._silent, 'content:', recoveredParsed.content)

                          // 只累积非静默分支的内容
                          if (!recoveredParsed._silent) {
                            fullContent += recoveredParsed.content
                            setStreamingContent(fullContent)
                            console.log('📝 [Binder Recovery] 累积内容，当前长度:', fullContent.length)
                          }
                        } else {
                          // 非 Binder 内容，直接累积
                          fullContent += recoveredParsed.content
                          setStreamingContent(fullContent)
                          console.log('📝 [Recovery] 累积内容，当前长度:', fullContent.length)
                        }
                      } else if (recoveredParsed.type === 'tool_call') {
                        // 工具调用事件，记录但不影响内容流
                        console.log('🔧 [Recovery] tool_call 事件:', JSON.stringify(recoveredParsed.tools || []))
                        
                        // 🎯 显示工具调用状态（恢复解析时）
                        if (recoveredParsed.tools && recoveredParsed.tools.length > 0) {
                          const tool = recoveredParsed.tools[0]
                          const toolName = tool.tool_name || 'unknown'
                          const toolArgs = tool.arguments || {}
                          
                          let description = `正在执行 ${toolName}`
                          if (toolName === 'play_audio' && toolArgs.audio_id) {
                            description = '🎵 正在播放音频...'
                          } else if (toolName.includes('search')) {
                            description = '🔍 正在搜索...'
                          } else if (toolName.includes('query')) {
                            description = '📋 正在查询...'
                          }
                          
                          // 🔧 恢复解析时也要清除思考状态
                          setThinkingStep(null)
                          setToolProgress({
                            tool: toolName,
                            description: description,
                            step: 1
                          })
                        }
                      }
                    } catch (splitError) {
                      console.error('🚨 [Flow Execute] 分割后仍然解析失败:', splitError, 'jsonStr:', jsonStr)
                    }
                  }
                }
              }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ [Flow Execute] Flow error:', error)
      
      // 提供更详细的错误信息
      let errorMsg = '未知错误'
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        errorMsg = '无法连接到后端服务，请确认：\n1. Agent Admin Service已启动（端口8001）\n2. CORS配置正确\n3. 网络连接正常'
      } else if (error instanceof Error) {
        errorMsg = error.message
      }
      
      const errorMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: "assistant",
        content: `❌ 请求失败: ${errorMsg}`,
        timestamp: new Date(),
      }
      setMessages(prev => [...prev, errorMessage])
      setStreamingContent("")
    } finally {
      setIsLoading(false)
      setCurrentNode("")
      // v11.3 修复：确保流程结束时清除节点激活状态并刷新队列
      // 即使 flow_end 未收到或动画队列延迟，也不会让节点一直转圈
      flushAnimationQueue()
      // 清除 thinking 和 tool_progress 状态
      setThinkingStep(null)
      setToolProgress(null)
    }
  }

  // 清空消息时同时生成新的 sessionId，表示开始一个新会话
  const handleClear = () => {
    setMessages([])
    setStreamingContent("")
    setSessionId(crypto.randomUUID())
    // 清空 ASR 状态
    setAsrInterimText('')
    // 停止语音相关功能
    if (vad.isListening) {
      vad.stop()
    }
    audioPlayer.stop()
    if (voiceWs.isConnected) {
      voiceWs.disconnect()
    }
    console.log('🔄 [TestPanel] 清空消息并生成新的 sessionId')
  }

  // v8.1: API 版本切换处理
  const handleApiVersionChange = (newVersion: 'v1' | 'v2') => {
    console.log('[TestPanel] 切换 API 版本:', apiVersion, '->', newVersion)

    // 1. 断开现有连接
    if (voiceWs?.isConnected) {
      voiceWs.disconnect()
    }

    // 2. 清空消息历史
    setMessages([])
    setStreamingContent("")
    setSessionId(crypto.randomUUID())

    // 3. 更新版本并持久化
    setApiVersion(newVersion)
    localStorage.setItem(`axiom_admin_voice_api_version_${projectId}`, newVersion)

    // 4. v2 首次展开架构说明
    if (newVersion === 'v2' && !hasSeenV2Arch) {
      setShowArchInfo(true)
      setHasSeenV2Arch(true)
      localStorage.setItem(`has_seen_v2_arch_${projectId}`, 'true')
    }

    // 5. 提示用户（使用 console 提示）
    console.log(`✅ 已切换到 ${newVersion} 模式`)
  }

  // V6.0: 开始录音
  // V6.0: 点击麦克风按钮
  const handleVoiceButtonClick = useCallback(async () => {
    console.log('[Voice] 🎤 handleVoiceButtonClick called', {
      isVoiceEnabled,
      voiceInputMode,
      'voiceWs.isConnected': voiceWs.isConnected,
      'vad.isListening': vad.isListening,
    })
    
    if (voiceInputMode === 'off') {
      // 首次点击，显示模式选择气泡
      console.log('[Voice] 显示模式选择气泡')
      setShowVoiceModePopover(true)
    } else if (voiceInputMode === 'vad') {
      // VAD 模式下点击，切换 VAD 开启/关闭
      if (vad.isListening) {
        console.log('[Voice] VAD 正在监听，停止 VAD')
        vad.stop()
        audioPlayer.stop()
      } else {
        console.log('[Voice] 启动 VAD 模式')
        if (!voiceWs.isConnected) {
          console.log('[Voice] WebSocket 未连接，正在连接...')
          voiceWs.connect()
        }
        const started = await vad.start()
        if (!started) {
          console.error('[Voice] VAD 启动失败')
        } else {
          console.log('[Voice] VAD 启动成功')
        }
      }
    } else if (voiceInputMode === 'ptt') {
      console.log('[Voice] PTT 模式，点击不做任何事（由长按触发）')
    }
  }, [isVoiceEnabled, voiceInputMode, vad, audioPlayer, voiceWs])

  // V6.0: 选择语音模式
  const handleVoiceModeSelect = useCallback(async (mode: 'ptt' | 'vad' | 'vad_interrupt') => {
    console.log('[Voice] 🎯 handleVoiceModeSelect called, mode:', mode)
    console.log('[Voice] 当前voiceInputMode:', voiceInputMode, '即将设置为:', mode)
    setVoiceInputMode(mode)
    setShowVoiceModePopover(false)
    
    if (mode === 'vad' || mode === 'vad_interrupt') {
      // 立即启动 VAD
      console.log(`[Voice] ${mode} 模式，准备连接 WebSocket 和启动 VAD`)
      if (!voiceWs.isConnected) {
        console.log('[Voice] WebSocket 未连接，正在连接...')
        voiceWs.connect()
      }
      const started = await vad.start()
      if (!started) {
        console.error('[Voice] VAD 启动失败，请检查麦克风权限')
        setVoiceInputMode('off')  // 回退到关闭状态
      } else {
        console.log('[Voice] VAD 启动成功')
        if (mode === 'vad_interrupt') {
          console.log('[Voice] vad_interrupt 模式：TTS 播放时可说话打断')
        }
      }
    } else if (mode === 'ptt') {
      console.log('[Voice] PTT 模式已选择，请长按麦克风按钮说话')
      // 🔧 确保WebSocket连接（PTT模式也需要）
      if (!voiceWs.isConnected) {
        console.log('[Voice] PTT模式：WebSocket 未连接，正在连接...')
        voiceWs.connect()
      }
    }
  }, [voiceWs, vad])
  
  // V6.0: 保存 VAD 灵敏度到 localStorage
  const handleVadSensitivityChange = useCallback((value: number) => {
    setVadSensitivity(value)
    localStorage.setItem(`axiom_vad_sensitivity_${projectId}`, String(value))
  }, [projectId])

  // V6.0: 实际开始录音的函数
  const startRecordingInternal = useCallback(async (clientY: number) => {
    if (!isVoiceEnabled || isRecording) return
    
    // 记录起始位置（用于判断上滑取消）
    recordingStartYRef.current = clientY
    recordingCancelledRef.current = false
    setRecordingCancelled(false)
    setShowRecordingOverlay(true)
    
    // 提前确保 WebSocket 已连接（不在录音结束后再连，减少延迟）
    const ws = voiceWsRef.current
    if (ws && !ws.isConnected) {
      console.log('[Voice] 提前连接 WebSocket')
      ws.connect()
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      })
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : 'audio/webm'
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      audioChunksRef.current = []
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
        stream.getTracks().forEach(track => track.stop())
        
        // 使用 ref 获取最新的取消状态（避免闭包问题）
        const wasCancelled = recordingCancelledRef.current
        console.log('[Voice] Recording stopped, audio size:', audioBlob.size, 'cancelled:', wasCancelled)
        
        // 如果没有被取消，发送录音
        if (audioBlob.size > 0 && !wasCancelled) {
          try {
            // 将 webm 转换为 PCM 格式（ASR 需要 PCM 而不是 webm）
            const arrayBuffer = await audioBlob.arrayBuffer()
            const audioContext = new AudioContext({ sampleRate: 16000 })
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
            
            // 获取 PCM 数据 (Float32Array)
            const pcmFloat32 = audioBuffer.getChannelData(0)
            
            // 转换为 Int16 PCM
            const pcmInt16 = new Int16Array(pcmFloat32.length)
            for (let i = 0; i < pcmFloat32.length; i++) {
              const s = Math.max(-1, Math.min(1, pcmFloat32[i]))
              pcmInt16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
            }
            
            // 转换为 Base64
            const base64Audio = btoa(
              new Uint8Array(pcmInt16.buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
            )
            
            // 关闭 AudioContext
            await audioContext.close()
            
            // 使用 ref 获取最新的 voiceWs（避免闭包问题）
            let ws = voiceWsRef.current
            if (!ws) {
              console.error('[Voice] WebSocket ref 未初始化')
              return
            }
            
            // 确保 WebSocket 已连接（最多等待 3 秒）
            if (!ws.isConnectedRef?.current) {
              console.log('[Voice] WebSocket 未连接，正在连接...')
              ws.connect()
              // 轮询等待连接建立（最多等待 3 秒）
              for (let i = 0; i < 30; i++) {
                await new Promise(resolve => setTimeout(resolve, 100))
                // 每次循环都重新获取最新的 ref
                ws = voiceWsRef.current
                if (ws?.isConnectedRef?.current) {
                  console.log('[Voice] WebSocket 连接成功，用时', (i + 1) * 100, 'ms')
                  break
                }
              }
            }
            
            // 重新获取最新的 ws 引用
            ws = voiceWsRef.current
            
            // 通过 WebSocket 发送 PCM 格式音频
            if (ws?.isConnectedRef?.current) {
              console.log('[Voice] 发送音频, PCM 长度:', base64Audio.length, 'bytes')
              const startSent = ws.sendAudioStart()
              const audioSent = ws.sendAudio(base64Audio, 'pcm', 16000)  // 发送 PCM 格式
              const endSent = ws.sendAudioEnd()
              console.log('[Voice] 发送结果: start=', startSent, 'audio=', audioSent, 'end=', endSent)
              // 添加用户语音消息占位符（ASR 返回后会更新）
              const userMsgId = `msg-${Date.now()}`
              currentUserMsgIdRef.current = userMsgId
              const userMessage: ChatMessage = {
                id: userMsgId,
                role: "user",
                content: "🎤 语音识别中...",
                timestamp: new Date(),
              }
              setMessages(prev => [...prev, userMessage])
              setIsLoading(true)
            } else {
              console.error('[Voice] WebSocket 连接超时，无法发送音频')
              // 降级到文字提示
              const userMessage: ChatMessage = {
                id: `msg-${Date.now()}`,
                role: "user",
                content: "🎤 [语音消息] (WebSocket 连接超时，请检查网络后重试)",
                timestamp: new Date(),
              }
              setMessages(prev => [...prev, userMessage])
            }
          } catch (err) {
            console.error('[Voice] 音频处理失败:', err)
            const userMessage: ChatMessage = {
              id: `msg-${Date.now()}`,
              role: "user",
              content: "🎤 [语音消息] (处理失败，请重试)",
              timestamp: new Date(),
            }
            setMessages(prev => [...prev, userMessage])
          }
        }
        
        audioChunksRef.current = []
      }
      
      mediaRecorderRef.current = mediaRecorder
      mediaRecorder.start(100)
      setIsRecording(true)
      console.log('[Voice] Recording started')
    } catch (err) {
      console.error('[Voice] Failed to start recording:', err)
      setShowRecordingOverlay(false)
      alert('无法启动录音，请检查麦克风权限')
    }
  }, [isVoiceEnabled, isRecording])

  // V6.0: 长按开始录音 (PTT 模式) - 使用计时器区分点击和长按
  const handleVoiceLongPressStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    console.log('[Voice] 🔽 handleVoiceLongPressStart called', {
      isVoiceEnabled,
      isRecording,
      voiceInputMode,
    })
    
    if (!isVoiceEnabled || isRecording || voiceInputMode !== 'ptt') {
      console.log('[Voice] 长按条件不满足，跳过', {
        isVoiceEnabled,
        isRecording,
        voiceInputMode,
        shouldSkip: !isVoiceEnabled || isRecording || voiceInputMode !== 'ptt'
      })
      return
    }
    
    // 获取坐标
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    console.log('[Voice] 设置长按计时器 (200ms)')
    
    // 设置长按计时器（200ms 后才认为是长按）
    longPressTimerRef.current = setTimeout(() => {
      console.log('[Voice] 长按计时器触发，开始录音')
      startRecordingInternal(clientY)
    }, 200)
  }, [isVoiceEnabled, isRecording, voiceInputMode, startRecordingInternal])

  // V6.0: 长按结束录音 (PTT 模式)
  const handleVoiceLongPressEnd = useCallback(() => {
    // 取消长按计时器（如果是短按，计时器还没触发就松开了）
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
    
    if (!isRecording || !mediaRecorderRef.current) return
    
    mediaRecorderRef.current.stop()
    setIsRecording(false)
    setShowRecordingOverlay(false)
    console.log('[Voice] Recording stopped by long press end')
  }, [isRecording])

  // V6.0: 录音时移动手指（用于检测上滑取消）
  const handleRecordingMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isRecording) return
    
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    const deltaY = recordingStartYRef.current - clientY
    
    // 上滑超过 50px 判定为取消
    const shouldCancel = deltaY > 50
    recordingCancelledRef.current = shouldCancel  // 同时更新 ref
    setRecordingCancelled(shouldCancel)
  }, [isRecording])

  // V6.0: 停止 VAD 模式
  const stopVADMode = useCallback(() => {
    vad.stop()
    audioPlayer.stop()
    setVoiceInputMode('off')
  }, [vad, audioPlayer])
  
  // V6.0: 播放消息中的音频（点击重播按钮）
  const handlePlayAudio = useCallback((audioData: string, format: AudioFormat) => {
    console.log('[Voice] 播放音频, 格式:', format, '长度:', audioData.length)
    // 重置会话统计（单次完整音频重播）
    audioPlayer.resetSessionStats()
    audioPlayer.queueAudio(audioData, format)
    // 标记流结束（这是完整音频，不是流式）
    audioPlayer.flush()
  }, [audioPlayer])
  
  // V6.0: 语音输入 UI（renderFooter）
  // 自定义头部：包含标题 + 折叠式测试选项
  const renderCustomHeader = useCallback(() => (
    <div className="space-y-2">
      {/* 标题栏 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
            mode === 'device' 
              ? 'bg-gradient-to-br from-emerald-500 to-teal-600' 
              : 'bg-gradient-to-br from-violet-500 to-purple-600'
          }`}>
            {mode === 'device' ? (
              <Smartphone className="h-3.5 w-3.5 text-white" />
            ) : (
              <MessageSquare className="h-3.5 w-3.5 text-white" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className="font-medium text-sm text-slate-800">
                {mode === 'device' ? '设备可用性测试' : (projectName || '测试对话')}
              </h3>
              {mode === 'device' && (
                <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4 bg-emerald-100 text-emerald-700">
                  Runtime v2
                </Badge>
              )}
              {isVoiceEnabled && (
                <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4 bg-blue-100 text-blue-700">
                  <Mic className="h-2.5 w-2.5 mr-0.5" />
                  语音
                </Badge>
              )}
            </div>
            <p className="text-[10px] text-slate-400">
              {mode === 'device' 
                ? `${deviceTestConfig?.deviceId || ''} · ${deviceTestConfig?.deviceName || ''}` 
                : `${agents.length} 个 Agent · ${routers.length} 个路由`
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-slate-500 hover:text-slate-700 text-xs"
            onClick={handleClear}
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            清空
          </Button>
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-slate-500 hover:text-slate-700"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      
      {/* v8.2: 设备测试模式显示性能指标 */}
      {mode === 'device' && (metrics.ttft || metrics.totalTime) && (
        <div className="flex items-center gap-4 py-2 px-3 bg-emerald-50 rounded-lg border border-emerald-100">
          <div className="flex items-center gap-2 text-xs">
            <Zap className="h-3.5 w-3.5 text-violet-600" />
            <span className="text-slate-600">首包耗时:</span>
            <span className="font-mono font-semibold text-violet-600">
              {metrics.ttft ? `${metrics.ttft}ms` : '-'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Clock className="h-3.5 w-3.5 text-blue-600" />
            <span className="text-slate-600">完整耗时:</span>
            <span className="font-mono font-semibold text-blue-600">
              {metrics.totalTime ? `${(metrics.totalTime / 1000).toFixed(2)}s` : '-'}
            </span>
          </div>
        </div>
      )}

      {/* 折叠式测试选项（设备测试模式隐藏） */}
      {mode !== 'device' && (
      <div>
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 py-1"
          onClick={() => setShowTestOptions(prev => !prev)}
        >
          <ChevronRight className={`h-3 w-3 transition-transform ${showTestOptions ? 'rotate-90' : ''}`} />
          <Settings className="h-3 w-3" />
          <span>测试选项</span>
          {deviceId && <span className="text-violet-500">（已配置）</span>}
        </button>
        {showTestOptions && <div className="pt-2">
          <div className="bg-slate-50 rounded-lg p-3 space-y-3">
            {/* v8.1: API 版本选择器 */}
            <div className="space-y-1">
              <Label htmlFor="api-version" className="text-xs text-slate-600 font-medium">
                API 版本
              </Label>
              <Select value={apiVersion} onValueChange={handleApiVersionChange}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel className="text-xs">选择测试模式</SelectLabel>
                    <SelectItem value="v1">
                      <div className="flex flex-col gap-0.5 py-0.5">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-xs">v1 - 普通模式</span>
                          <Badge variant="outline" className="text-[9px] px-1 py-0">默认</Badge>
                        </div>
                        <div className="text-[10px] text-slate-500">JWT Token 认证 · 同步工具执行</div>
                      </div>
                    </SelectItem>
                    <SelectItem value="v2">
                      <div className="flex flex-col gap-0.5 py-0.5">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-xs">v2 - Webhook 模式</span>
                          <Badge variant="secondary" className="text-[9px] px-1 py-0">设备专用</Badge>
                        </div>
                        <div className="text-[10px] text-slate-500">异步 Webhook 回调 · device_command</div>
                      </div>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {/* v8.3: VAD Provider 选择器 */}
            <div className="space-y-1">
              <Label htmlFor="vad-provider" className="text-xs text-slate-600 font-medium">
                VAD 检测位置
              </Label>
              <Select 
                value={vadProvider} 
                onValueChange={(value: 'client' | 'server') => {
                  setVadProvider(value)
                  localStorage.setItem(`axiom_vad_provider_${projectId}`, value)
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel className="text-xs">选择 VAD 检测位置</SelectLabel>
                    <SelectItem value="client">
                      <div className="flex flex-col gap-0.5 py-0.5">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-xs">客户端 VAD</span>
                          <Badge variant="outline" className="text-[9px] px-1 py-0">默认</Badge>
                        </div>
                        <div className="text-[10px] text-slate-500">本地检测 · audio_end 触发</div>
                      </div>
                    </SelectItem>
                    <SelectItem value="server">
                      <div className="flex flex-col gap-0.5 py-0.5">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-xs">云端 VAD</span>
                          <Badge variant="secondary" className="text-[9px] px-1 py-0 text-amber-600 border-amber-300">收费</Badge>
                        </div>
                        <div className="text-[10px] text-slate-500">云端检测 · is_final 触发</div>
                      </div>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400">仅在 VAD 模式下生效</p>
            </div>

            {/* v8.1: v2 架构说明卡片 */}
            {apiVersion === 'v2' && (
              <div>
                <div className="border border-blue-200 rounded-lg overflow-hidden bg-blue-50/50">
                  <button
                    type="button"
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-blue-50 transition-colors"
                    onClick={() => setShowArchInfo(prev => !prev)}
                  >
                    <div className="flex items-center gap-1.5 text-xs font-medium text-blue-900">
                      <Info className="h-3 w-3" />
                      <span>v2 架构说明</span>
                      <Badge variant="outline" className="text-[9px] border-blue-300 text-blue-700 px-1">重要</Badge>
                    </div>
                    <ChevronDown className={`h-3 w-3 text-blue-600 transition-transform ${showArchInfo ? 'rotate-180' : ''}`} />
                  </button>
                  {showArchInfo && <div>
                    <div className="px-3 pb-3 space-y-2 text-[10px]">
                      <div className="bg-white rounded p-2 border border-blue-100">
                        <div className="font-semibold text-slate-800 mb-1 flex items-center gap-1">
                          <Key className="h-2.5 w-2.5 text-violet-600" />
                          设备认证方式变更
                        </div>
                        <div className="text-slate-600 space-y-0.5">
                          <div>• v1: JWT Token (用户认证)</div>
                          <div>• v2: Device ID + Secret (设备凭证)</div>
                          <div className="text-amber-700 mt-1">⚠️ Admin v2 测试用 JWT，Runtime v2 用设备凭证</div>
                        </div>
                      </div>
                      <div className="bg-white rounded p-2 border border-blue-100">
                        <div className="font-semibold text-slate-800 mb-1 flex items-center gap-1">
                          <Zap className="h-2.5 w-2.5 text-amber-600" />
                          Webhook 回调机制
                        </div>
                        <div className="text-slate-600 space-y-0.5">
                          <div>• MCP 工具 → Webhook 异步回调</div>
                          <div>• 发送 device_command 到设备</div>
                          <div>• 解决 SSE 短连接无法接收指令问题</div>
                        </div>
                      </div>
                    </div>
                  </div>}
                </div>
              </div>
            )}

            {/* 原有的 Device ID 输入框 */}
            <div className="space-y-1">
              <Label htmlFor="device-id" className="text-xs text-slate-600">
                Device ID（可选）
              </Label>
              <Input
                id="device-id"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                placeholder="如：device_12345"
                className="h-8 text-xs"
              />
              <p className="text-[10px] text-slate-400">
                用于 IoT 设备控制场景，如跑步机
              </p>
              {/* 历史记录标签 */}
              {deviceIdHistory.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {deviceIdHistory.map((id) => (
                    <div
                      key={id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-white border rounded-full cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => setDeviceId(id)}
                    >
                      <span className="max-w-[100px] truncate">{id}</span>
                      <button
                        type="button"
                        className="hover:text-red-500 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation()
                          removeDeviceIdFromHistory(id)
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* v10.0 新增：Identity ID 输入框（身份模拟所需） */}
            <div className="space-y-1">
              <Label htmlFor="identity-id" className="text-xs text-slate-600 flex items-center gap-1">
                <span>🎭</span>
                Identity ID
                <span className="text-slate-400">（可选）</span>
              </Label>
              <Input
                id="identity-id"
                value={identityId}
                onChange={(e) => setIdentityId(e.target.value)}
                placeholder="如：identity_a253e5fe148f"
                className="h-8 text-xs"
              />
              <p className="text-[10px] text-slate-400">
                用于身份模拟场景，PersonaAgent 节点需要此参数
              </p>
            </div>
          </div>
        </div>}
      </div>
      )}
    </div>
  ), [mode, projectName, agents, routers, showTestOptions, setShowTestOptions, deviceId, apiVersion, handleApiVersionChange, vadProvider, setVadProvider, projectId, showArchInfo, setShowArchInfo, deviceIdHistory, removeDeviceIdFromHistory, identityId, setIdentityId])

  // V6.0: 渲染 VAD 状态条（底部浮动）
  const renderVADStatusBar = useCallback(() => {
    // 支持 vad 和 vad_interrupt 两种模式
    if (!isVoiceEnabled || (voiceInputMode !== 'vad' && voiceInputMode !== 'vad_interrupt') || !vad.isListening) return null
    
    const isInterruptMode = voiceInputMode === 'vad_interrupt'
    const bgColor = isInterruptMode ? 'bg-amber-50' : 'bg-green-50'
    const borderColor = isInterruptMode ? 'border-amber-200' : 'border-green-200'
    const dotColor = isRecording ? 'bg-red-500 animate-pulse' : (isInterruptMode ? 'bg-amber-500' : 'bg-green-500')
    const textColor = isInterruptMode ? 'text-amber-700' : 'text-green-700'
    const btnBgColor = isInterruptMode ? 'bg-amber-100 hover:bg-amber-200' : 'bg-green-100 hover:bg-green-200'
    
    return (
      <div className={`px-3 py-2 ${bgColor} border-t ${borderColor} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${dotColor}`} />
          <span className={`text-xs ${textColor} font-medium`}>
            {isRecording ? '正在聆听...' : '等待说话'}
            {isInterruptMode && !isRecording && ' (可打断)'}
          </span>
          {asrInterimText && (
            <span className="text-xs text-blue-600 italic max-w-[150px] truncate">
              {asrInterimText}
            </span>
          )}
        </div>
        <button
          onClick={stopVADMode}
          className={`text-xs px-2 py-1 rounded ${btnBgColor} ${textColor} transition-colors`}
        >
          停止
        </button>
      </div>
    )
  }, [isVoiceEnabled, voiceInputMode, vad.isListening, isRecording, asrInterimText, stopVADMode])

  return (
    <>
      {/* V6.0: 全屏录音 Overlay (PTT 模式) */}
      {showRecordingOverlay && (
        <div 
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center"
          onMouseMove={handleRecordingMove}
          onTouchMove={handleRecordingMove}
          onMouseUp={handleVoiceLongPressEnd}
          onTouchEnd={handleVoiceLongPressEnd}
        >
          <div className={`flex flex-col items-center transition-all ${recordingCancelled ? 'scale-90 opacity-80' : ''}`}>
            {/* 麦克风图标和波形 */}
            <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-4 transition-colors ${
              recordingCancelled ? 'bg-red-500' : 'bg-blue-500'
            }`}>
              {recordingCancelled ? (
                <X className="h-12 w-12 text-white" />
              ) : (
                <Mic className="h-12 w-12 text-white animate-pulse" />
              )}
            </div>
            
            {/* 波形动画 - 使用固定高度和 CSS animation */}
            {!recordingCancelled && (
              <div className="flex items-center gap-1 mb-4 h-8">
                {[12, 20, 28, 16, 24].map((height, i) => (
                  <div
                    key={i}
                    className="w-1 bg-white rounded-full"
                    style={{
                      height: `${height}px`,
                      animation: `voiceWave 0.5s ease-in-out infinite alternate`,
                      animationDelay: `${i * 0.1}s`,
                    }}
                  />
                ))}
              </div>
            )}
            <style>{`
              @keyframes voiceWave {
                from { transform: scaleY(0.5); }
                to { transform: scaleY(1.2); }
              }
            `}</style>
            
            {/* 提示文字 */}
            <p className={`text-white text-lg font-medium mb-2 ${recordingCancelled ? 'text-red-300' : ''}`}>
              {recordingCancelled ? '松开取消' : '松开 发送'}
            </p>
            <p className="text-white/60 text-sm">
              {recordingCancelled ? '' : '↑ 上滑取消'}
            </p>
            
            {/* ASR 中间结果 */}
            {asrInterimText && !recordingCancelled && (
              <div className="mt-4 px-4 py-2 bg-white/10 rounded-lg max-w-[250px]">
                <p className="text-white text-sm text-center">{asrInterimText}</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* V6.0: 模式选择气泡 */}
      {showVoiceModePopover && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setShowVoiceModePopover(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowVoiceModePopover(false)
            }
          }}
          tabIndex={0}
          role="dialog"
          aria-modal="true"
          aria-label="选择语音输入模式"
        >
          <div 
            className="absolute bottom-24 right-8 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden min-w-[200px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-2 bg-slate-50 border-b text-xs text-slate-500 font-medium">
              选择语音输入模式
            </div>
            <button
              onClick={() => handleVoiceModeSelect('ptt')}
              className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-50 flex items-center gap-3 border-b ${voiceInputMode === 'ptt' ? 'bg-blue-50' : ''}`}
            >
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <Mic className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-slate-800">按住说话</div>
                <div className="text-xs text-slate-500">长按麦克风录音</div>
              </div>
              {voiceInputMode === 'ptt' && <Check className="h-4 w-4 text-blue-600" />}
            </button>
            <button
              onClick={() => handleVoiceModeSelect('vad')}
              className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-50 flex items-center gap-3 border-b ${voiceInputMode === 'vad' ? 'bg-green-50' : ''}`}
            >
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <Radio className="h-4 w-4 text-green-600" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-slate-800">自动检测</div>
                <div className="text-xs text-slate-500">自动识别语音开始/结束</div>
              </div>
              {voiceInputMode === 'vad' && <Check className="h-4 w-4 text-green-600" />}
            </button>
            {/* V6.0: 可打断的自动检测模式 */}
            <div className={`${voiceInputMode === 'vad_interrupt' ? 'bg-amber-50' : ''}`}>
              <button
                onClick={() => handleVoiceModeSelect('vad_interrupt')}
                className="w-full px-4 py-3 text-left text-sm hover:bg-slate-50 flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-amber-600" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-slate-800">自动检测 (可打断)</div>
                  <div className="text-xs text-slate-500">说话可打断 AI 回复</div>
                </div>
                {voiceInputMode === 'vad_interrupt' && <Check className="h-4 w-4 text-amber-600" />}
              </button>
              {/* 灵敏度调整（仅选中 vad_interrupt 时显示） */}
              {voiceInputMode === 'vad_interrupt' && (
                <div className="px-4 pb-3 pt-1 border-t bg-amber-50/50">
                  <div className="flex items-center justify-between text-xs text-slate-600 mb-2">
                    <span>灵敏度</span>
                    <span className="font-medium">
                      {vadSensitivity <= 0.4 ? '低' : vadSensitivity <= 0.6 ? '中' : vadSensitivity <= 0.8 ? '较高' : '高'}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0.3"
                    max="0.9"
                    step="0.1"
                    value={vadSensitivity}
                    onChange={(e) => handleVadSensitivityChange(parseFloat(e.target.value))}
                    className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                  <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                    <span>低</span>
                    <span>高</span>
                  </div>
                  <div className="mt-2 text-[10px] text-amber-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>效果因设备而异</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <ChatPanel
        messages={messages}
        isLoading={isLoading}
        streamingContent={streamingContent}
        executionPath={executionPath}
        currentNode={currentNode}
        routeInfo={routeInfo || undefined}
        variant="agent"
        theme="light"
        renderHeader={renderCustomHeader}
        renderFooter={renderVADStatusBar}
        onSend={handleSend}
        onClear={handleClear}
        onClose={onClose}
        placeholder={voiceInputMode === 'ptt' ? "输入消息或长按麦克风说话..." : "输入消息..."}
        emptyStateTitle="开始测试"
        emptyStateDescription="发送消息测试 Agent 流程"
        emptyStateSuggestions={['你好', '介绍一下你自己', '你能做什么？']}
        autoFocus={true}
        // V6.0: 语音输入
        showVoiceButton={isVoiceEnabled}
        voiceMode={voiceInputMode}
        isRecording={isRecording}
        onVoiceClick={handleVoiceButtonClick}
        onVoiceLongPressStart={handleVoiceLongPressStart}
        onVoiceLongPressEnd={handleVoiceLongPressEnd}
        // V6.0: 语音播放
        isAudioPlaying={audioPlayer.isPlaying}
        onPlayAudio={handlePlayAudio}
        // v8.2: 设备测试模式使用全宽布局
        fullWidth={mode === 'device'}
        // v11.3: Structured Agent 状态指示（GPT 风格：小号 + 图标 + 浅色）
        // 🔧 改进：优化状态显示逻辑，避免冲突
        agentStatus={
          toolProgress
            ? { type: 'tool' as const, text: toolProgress.description || `正在执行 ${toolProgress.tool}...` }
            : thinkingStep
              ? { type: 'thinking' as const, text: `思考中 · 步骤 ${thinkingStep.step}` }
              : null
        }
        streamingReasoning={reasoningContent || undefined}
      />
    </>
  )
})
TestPanel.displayName = 'TestPanel'


// 主画布组件
function FlowCanvas() {
  const { id } = useParams()
  const navigate = useNavigate()
  
  // 从 store 获取项目数据
  const { projects, setCurrentProject, updateProjectNodes, updateProject, addProject } = useProjectStore()
  
  // 获取可用模型列表
  const { modelsByProvider, defaultModel } = useModels()
  // 获取项目可用工具（v4.0：改为按项目获取，而非所有系统工具）
  // refetch: 静默刷新工具列表，用于页面重新可见时更新数据
  const { tools, builtinTools, isLoading: isLoadingTools, refetch: refetchTools } = useTools(id)
  
  // v10.3: 场景配置列表（从 Identity Engine API 动态获取）
  const [sceneConfigs, setSceneConfigs] = useState<SceneConfig[]>([])
  
  // 加载场景配置列表
  useEffect(() => {
    const loadSceneConfigs = async () => {
      try {
        const scenes = await listScenes()
        setSceneConfigs(scenes)
      } catch (error) {
        console.error('Failed to load scene configs:', error)
        // 如果加载失败，使用空数组（UI 会显示 scene_id 原始值）
      }
    }
    loadSceneConfigs()
  }, [])
  
  // 右侧面板状态: 'none' | 'config' | 'test'
  const [rightPanel, setRightPanel] = useState<'none' | 'config' | 'test' | 'skills'>('none')
  
  // System Prompt 编辑器状态
  const [promptEditorOpen, setPromptEditorOpen] = useState(false)
  const [promptEditorContent, setPromptEditorContent] = useState('')
  const [promptEditorLanguage, setPromptEditorLanguage] = useState<'markdown' | 'yaml'>('markdown')
  const [promptEditorField, setPromptEditorField] = useState<string>('prompt')
  const [promptEditorTitle, setPromptEditorTitle] = useState<string>('')
  const [promptEditorDescription, setPromptEditorDescription] = useState<string>('')
  
  
  // 使用 state 跟踪当前项目ID，确保保存后立即更新（不依赖URL参数）
  const [currentProjectId, setCurrentProjectId] = useState<string | undefined>(id)
  
  // 当URL参数变化时，更新当前项目ID
  useEffect(() => {
    if (id) {
      setCurrentProjectId(id)
    }
  }, [id])
  
  // 找到当前项目（优先使用currentProjectId，如果不存在则使用id）
  const effectiveProjectId = currentProjectId || id
  const project = projects.find(p => p.id === effectiveProjectId)
  
  // 如果项目不存在，尝试从后端加载项目列表
  useEffect(() => {
    if (effectiveProjectId && !project) {
      console.log('⚠️ [ProjectStudio] 项目不在本地store中，尝试从后端加载:', effectiveProjectId)
      const loadProjectFromBackend = async () => {
        try {
          // 先尝试从后端获取项目列表
          const projectsResponse = await ProjectApiService.getProjects()
          if (projectsResponse.success && projectsResponse.data) {
            const backendProject = projectsResponse.data.find(p => p.id === effectiveProjectId)
            if (backendProject) {
              console.log('✅ [ProjectStudio] 从后端找到项目，添加到store:', backendProject.id)
              // 项目存在，添加到store
              addProject({
                ...backendProject,
                nodes: [],
                edges: [],
              })
              return
            }
          }
          
          // 如果后端也没有这个项目，重定向到首页
          console.warn('❌ [ProjectStudio] 项目不存在（本地store和后端都没有）:', effectiveProjectId)
          navigate('/', { replace: true })
        } catch (error) {
          console.error('❌ [ProjectStudio] 加载项目列表失败:', error)
          // 如果加载失败，也重定向到首页
          navigate('/', { replace: true })
        }
      }
      
      loadProjectFromBackend()
    }
  }, [effectiveProjectId, project, addProject, navigate])
  
  // 使用 ref 跟踪是否有未保存的更改
  const hasChangesRef = useRef(false)
  
  // 使用 ref 跟踪是否正在加载flow配置，避免重复加载
  const isLoadingFlowRef = useRef(false)
  
  // 使用 ref 跟踪当前加载的项目ID，用于取消请求
  const loadingProjectIdRef = useRef<string | null>(null)
  
  // 使用 ref 跟踪是否正在更新edges，防止visitedNodeIds effect干扰
  const isUpdatingEdgesRef = useRef(false)
  
  // 页面可见性变化时静默刷新工具列表
  // 遵循 Apple HIG：后台静默刷新，不打断用户操作
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && effectiveProjectId) {
        // 页面从隐藏变为可见时，静默刷新工具列表
        // 这样可以确保用户在其他页面删除工具后，返回时数据是最新的
        refetchTools()
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [effectiveProjectId, refetchTools])
  
  // 初始化节点和边
  // 如果项目没有nodes或nodes为空数组，使用默认的入口和出口节点
  // 注意：useNodesState 和 useEdgesState 的初始值只在组件首次挂载时使用
  // 如果 project 在挂载后更新，需要通过 useEffect 同步
  // 规范化节点：确保 position.x 和 position.y 是数字类型（React Flow 要求）
  // 后端可能返回字符串格式的坐标
  const normalizeNodes = (nodesToNormalize: Node[]): Node[] => {
    return nodesToNormalize.map((node: Node) => ({
      ...node,
      position: {
        x: typeof node.position?.x === 'string' 
          ? parseFloat(node.position.x) 
          : (node.position?.x || 0),
        y: typeof node.position?.y === 'string' 
          ? parseFloat(node.position.y) 
          : (node.position?.y || 0),
      },
    }))
  }
  
  const rawInitialNodes = project?.nodes && project.nodes.length > 0 ? project.nodes : getDefaultNodes()
  const initialNodes = normalizeNodes(rawInitialNodes)
  const initialEdges = project?.edges || []
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)
  
  // 当 project 变化时，同步初始值到 React Flow 状态（仅在 project 首次加载时）
  // 注意：这个 effect 只在 project 从 undefined 变为有值时执行一次
  // 但是，如果 loadFlowConfig 正在加载，不要覆盖（避免竞态条件）
  const projectLoadedRef = useRef<string | null>(null)
  useEffect(() => {
    if (project && project.id !== projectLoadedRef.current) {
      // 如果正在加载flow配置，跳过同步初始值（让loadFlowConfig处理）
      if (isLoadingFlowRef.current && loadingProjectIdRef.current === project.id) {
        console.log('⏸️ [ProjectStudio] 正在加载flow配置，跳过同步初始值')
        projectLoadedRef.current = project.id
        return
      }
      
      // 项目首次加载，同步初始值
      console.log('🔄 [ProjectStudio] 项目首次加载，同步初始nodes和edges:', {
        projectId: project.id,
        nodesCount: initialNodes.length,
        edgesCount: initialEdges.length,
      })
      
      // 只有在 React Flow 状态为空时才同步初始值
      // 如果已经有数据（从后端加载的），不要覆盖
      if (nodes.length === 0 && initialNodes.length > 0) {
        console.log('🔄 [ProjectStudio] 同步初始nodes到React Flow')
        setNodes(initialNodes)
      }
      if (edges.length === 0 && initialEdges.length > 0) {
        console.log('🔄 [ProjectStudio] 同步初始edges到React Flow')
        isUpdatingEdgesRef.current = true
        setEdges(initialEdges)
        setTimeout(() => {
          isUpdatingEdgesRef.current = false
        }, 100)
      }
      
      projectLoadedRef.current = project.id
    }
  }, [project?.id, initialNodes, initialEdges, nodes.length, edges.length, setNodes, setEdges])
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null)
  const [isSaved, setIsSaved] = useState(true)
  const [showSaveSuccess, setShowSaveSuccess] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoadingFlow, setIsLoadingFlow] = useState(false)
  
  // 发布状态
  const [isPublishing, setIsPublishing] = useState(false)
  const [isPublished, setIsPublished] = useState(false)
  const [showPublishSuccess, setShowPublishSuccess] = useState(false)
  
  // 启用记忆开关状态（v3.0 新增）
  const [memoryEnabled, setMemoryEnabled] = useState(false)
  const [userContextApi, setUserContextApi] = useState<string | undefined>(undefined) // 用户上下文 API
  const [isLoadingSettings, setIsLoadingSettings] = useState(false)
  const [isSavingSettings, setIsSavingSettings] = useState(false)
  
  // API Key 管理状态
  const [showSettingsDialog, setShowSettingsDialog] = useState(false)
  const [apiKeyData, setApiKeyData] = useState<{ id: string; api_key: string; created_at: string } | null>(null)
  const [isLoadingApiKey, setIsLoadingApiKey] = useState(false)
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState<string | null>(null) // 新生成的完整 Key（只显示一次）
  
  // Toast hook
  const { toasts, showSuccess, showError, removeToast } = useToast()
  
  // 执行状态（用于流程图动画）
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null)
  const [visitedNodeIds, setVisitedNodeIds] = useState<Set<string>>(new Set())

  // 获取选中的节点数据
  const selectedNode = useMemo(() => {
    if (!selectedNodeId) return null
    return nodes.find(n => n.id === selectedNodeId) || null
  }, [selectedNodeId, nodes])

  // 获取所有 Agent 节点
  const agentNodes = useMemo(() => nodes.filter(n => n.type === 'agent'), [nodes])
  

  // 使用 useMemo 确保 nodeTypes 和 defaultEdgeOptions 引用稳定
  // 即使它们在组件外部定义，React Flow 也需要在组件内部有稳定的引用
  const memoizedNodeTypes = useMemo(() => NODE_TYPES, [])
  const memoizedDefaultEdgeOptions = useMemo(() => DEFAULT_EDGE_OPTIONS, [])
  const memoizedConnectionLineStyle = useMemo(() => CONNECTION_LINE_STYLE, [])
  const memoizedSnapGrid = useMemo(() => SNAP_GRID, [])

  // 设置当前项目
  useEffect(() => {
    if (effectiveProjectId) {
      setCurrentProject(effectiveProjectId)
    }
  }, [effectiveProjectId, setCurrentProject])

  // 加载项目设置（v3.0 新增）
  useEffect(() => {
    if (!effectiveProjectId) return

    const loadProjectSettings = async () => {
      setIsLoadingSettings(true)
      try {
        const response = await ProjectApiService.getProjectSettings(effectiveProjectId)
        if (response.success && response.data) {
          setMemoryEnabled(response.data.memory_enabled ?? false)
          setUserContextApi(response.data.user_context_api) // 保存用户上下文 API
          console.log('✅ [ProjectStudio] 项目设置加载成功:', response.data)
        }
      } catch (error) {
        console.log('⚠️ [ProjectStudio] 加载项目设置失败（可能尚未设置）:', error)
        // 如果加载失败（404等），使用默认值 false
        setMemoryEnabled(false)
        setUserContextApi(undefined)
      } finally {
        setIsLoadingSettings(false)
      }
    }

    loadProjectSettings()
  }, [effectiveProjectId])

  // 切换记忆开关时保存设置
  const handleMemoryToggle = useCallback(async (enabled: boolean) => {
    if (!effectiveProjectId) return

    // 乐观更新：先更新UI，后台保存
    setMemoryEnabled(enabled)
    setIsSavingSettings(true)

    try {
      const response = await ProjectApiService.updateProjectSettings(effectiveProjectId, {
        memory_enabled: enabled,
      })
      if (response.success) {
        console.log('✅ [ProjectStudio] 项目设置保存成功:', response.data)
        showSuccess(enabled ? '已启用记忆功能' : '已关闭记忆功能')
      }
    } catch (error) {
      console.error('❌ [ProjectStudio] 保存项目设置失败:', error)
      // 回滚状态
      setMemoryEnabled(!enabled)
      showError('保存设置失败，请重试')
    } finally {
      setIsSavingSettings(false)
    }
  }, [effectiveProjectId, showSuccess, showError])

  // ==================== API Key 管理函数 ====================
  
  // 打开设置对话框时加载 API Key
  const handleOpenSettings = useCallback(async () => {
    setShowSettingsDialog(true)
    setNewlyGeneratedKey(null) // 清除之前生成的 Key
    
    if (!effectiveProjectId) return
    
    setIsLoadingApiKey(true)
    try {
      const response = await ProjectApiService.getApiKey(effectiveProjectId)
      console.log('🔑 [ProjectStudio] 获取 API Key 响应:', JSON.stringify(response, null, 2))
      if (response.success && response.data && response.data.api_key) {
        setApiKeyData(response.data)
      } else {
        // 没有 API Key
        console.log('🔑 [ProjectStudio] 项目没有 API Key')
        setApiKeyData(null)
      }
    } catch (error: unknown) {
      console.error('❌ [ProjectStudio] 获取 API Key 失败:', error)
      // 任何错误都显示"生成 API Key"按钮
      setApiKeyData(null)
    } finally {
      setIsLoadingApiKey(false)
    }
  }, [effectiveProjectId])

  // 生成 API Key
  const handleGenerateApiKey = useCallback(async () => {
    if (!effectiveProjectId) return
    
    setIsRegenerating(true)
    try {
      const response = await ProjectApiService.createApiKey(effectiveProjectId)
      if (response.success && response.data) {
        setApiKeyData(response.data)
        setNewlyGeneratedKey(response.data.api_key) // 保存完整 Key 供用户复制
        showSuccess('API Key 已生成，请立即复制保存')
      }
    } catch (error) {
      console.error('❌ [ProjectStudio] 生成 API Key 失败:', error)
      showError('生成 API Key 失败，请重试')
    } finally {
      setIsRegenerating(false)
      setShowRegenerateConfirm(false)
    }
  }, [effectiveProjectId, showSuccess, showError])

  // 删除 API Key
  const handleDeleteApiKey = useCallback(async () => {
    if (!effectiveProjectId) return
    
    console.log('🗑️ [ProjectStudio] 删除 API Key:', { projectId: effectiveProjectId })
    
    setIsDeleting(true)
    try {
      const response = await ProjectApiService.deleteApiKey(effectiveProjectId)
      if (response.success) {
        setApiKeyData(null)
        setNewlyGeneratedKey(null)
        showSuccess('API Key 已删除')
      }
    } catch (error) {
      console.error('❌ [ProjectStudio] 删除 API Key 失败:', error)
      showError('删除 API Key 失败，请重试')
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
    }
  }, [effectiveProjectId, showSuccess, showError])

  // 复制 API Key 到剪贴板
  const handleCopyApiKey = useCallback(async () => {
    const keyToCopy = newlyGeneratedKey || apiKeyData?.api_key
    if (!keyToCopy) return
    
    try {
      await navigator.clipboard.writeText(keyToCopy)
      showSuccess('已复制到剪贴板')
    } catch (error) {
      console.error('❌ [ProjectStudio] 复制失败:', error)
      showError('复制失败，请手动复制')
    }
  }, [newlyGeneratedKey, apiKeyData?.api_key, showSuccess, showError])

  // 格式化 API Key 显示（隐藏中间部分）
  const formatApiKey = (key: string) => {
    if (key.length <= 16) return key
    return `${key.slice(0, 12)}****${key.slice(-8)}`
  }

  // ==================== End API Key 管理函数 ====================

  // 退出页面时，如果有未保存的修改，提示用户
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChangesRef.current && !isSaved) {
        e.preventDefault()
        e.returnValue = '您有未保存的修改，确定要离开吗？'
        return e.returnValue
      }
    }
    
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isSaved])
  
  // 当项目切换时，从后端加载flow配置并重新初始化节点和边
  useEffect(() => {
    if (!effectiveProjectId || !project) {
      return
    }
    
    // 如果正在加载同一个项目，跳过
    if (isLoadingFlowRef.current && loadingProjectIdRef.current === effectiveProjectId) {
      return
    }
    
    // 标记开始加载
    isLoadingFlowRef.current = true
    loadingProjectIdRef.current = effectiveProjectId
    setIsLoadingFlow(true)
    
    const loadFlowConfig = async () => {
      // 检查项目是否已切换（避免竞态条件）
      const currentProjectId = effectiveProjectId
      
      try {
        // 尝试从后端加载flow配置
        const flowResponse = await FlowApiService.getFlowConfig(currentProjectId)
        
        // 再次检查项目是否已切换
        if (loadingProjectIdRef.current !== currentProjectId) {
          console.log('⚠️ [ProjectStudio] 项目已切换，取消加载flow配置')
          return
        }
        
        if (flowResponse.success && flowResponse.data) {
          const rawBackendNodes = flowResponse.data.nodes || []
          const backendEdges = flowResponse.data.edges || []
          
          // 规范化节点（position 数字化 + 默认值合并）
          const backendNodes = normalizeNodes(rawBackendNodes).map(applyNodeDefaults)
          
          // 如果后端有flow配置，使用后端的配置（这是唯一真实来源）
          // 注意：即使nodes为空，如果edges不为空，也应该使用后端的配置
          console.log('✅ [ProjectStudio] 从后端加载flow配置:')
          console.log('  nodesCount:', backendNodes.length)
          console.log('  edgesCount:', backendEdges.length)
          console.log('  hasNodes:', backendNodes.length > 0)
          console.log('  hasEdges:', backendEdges.length > 0)
          console.log('  rawEdges:', JSON.stringify(backendEdges, null, 2))
          console.log('  edges summary:', JSON.stringify(backendEdges.map((e: Edge) => ({ 
            id: e.id, 
            source: e.source, 
            target: e.target,
            sourceHandle: e.sourceHandle,
            targetHandle: e.targetHandle,
            markerEnd: e.markerEnd,
            style: e.style,
          })), null, 2))
          console.log('  nodes:', JSON.stringify(backendNodes.map(n => ({ id: n.id, type: n.type, position: n.position })), null, 2))
          
          // 确保edges有id字段（React Flow要求）
          const normalizedEdges = backendEdges.map((edge: Edge, index: number) => {
            console.log(`🔍 [ProjectStudio] 处理edge ${index}:`, {
              edge: JSON.parse(JSON.stringify(edge)),
              hasId: !!edge.id,
              source: edge.source,
              target: edge.target,
            })
            
            // 规范化样式：确保strokeWidth是数字类型（React Flow要求）
            const normalizedStyle = edge.style 
              ? (typeof edge.style === 'string' 
                  ? { stroke: "#94a3b8", strokeWidth: 2 } 
                  : {
                      ...edge.style,
                      strokeWidth: typeof edge.style.strokeWidth === 'string' 
                        ? parseInt(edge.style.strokeWidth, 10) 
                        : (edge.style.strokeWidth || 2),
                      stroke: edge.style.stroke || "#94a3b8",
                    })
              : { stroke: "#94a3b8", strokeWidth: 2 }
            
            // 如果edge没有id，生成一个
            if (!edge.id) {
              const generatedId = `edge-${edge.source}-${edge.target}-${index}`
              console.log(`⚠️ [ProjectStudio] Edge缺少id，生成: ${generatedId}`)
              return {
                ...edge,
                id: generatedId,
                markerEnd: edge.markerEnd || { type: MarkerType.ArrowClosed, color: '#94a3b8' },
                style: normalizedStyle,
              }
            }
            
            // 确保edge有必要的样式属性
            return {
              ...edge,
              markerEnd: edge.markerEnd || { type: MarkerType.ArrowClosed, color: '#94a3b8' },
              style: normalizedStyle,
            }
          })
          
          console.log('✅ [ProjectStudio] 规范化后的edges:', {
            edgesCount: normalizedEdges.length,
            edges: JSON.parse(JSON.stringify(normalizedEdges.map(e => ({ 
              id: e.id, 
              source: e.source, 
              target: e.target, 
              sourceHandle: e.sourceHandle,
              hasMarkerEnd: !!e.markerEnd,
              hasStyle: !!e.style,
              markerEnd: e.markerEnd,
              style: e.style,
            })))),
            fullEdges: JSON.parse(JSON.stringify(normalizedEdges)),
          })
          
          // 标记正在更新edges，防止visitedNodeIds effect干扰
          isUpdatingEdgesRef.current = true
          
          console.log('🔄 [ProjectStudio] 开始设置nodes和edges到React Flow状态:', {
            nodesCount: backendNodes.length,
            edgesCount: normalizedEdges.length,
            edgesIds: normalizedEdges.map(e => e.id),
          })
          
          // 始终使用后端的配置，确保数据一致性
          // 使用 requestAnimationFrame 确保在下一个渲染周期设置，避免与visitedNodeIds effect冲突
          requestAnimationFrame(() => {
            setNodes(backendNodes)
            setEdges(normalizedEdges)
            
            console.log('✅ [ProjectStudio] 已设置nodes和edges到React Flow状态')
            
            // 更新本地store，确保本地store与后端一致（使用规范化后的edges）
            updateProjectNodes(currentProjectId, backendNodes, normalizedEdges)
            
            console.log('✅ [ProjectStudio] 已更新本地store')
            
            // 标记项目已加载完成
            projectLoadedRef.current = currentProjectId
            
            setIsSaved(true)
            hasChangesRef.current = false
            setIsLoadingFlow(false)
            isLoadingFlowRef.current = false
            
            // 延迟重置标记，确保React Flow完成渲染
            setTimeout(() => {
              isUpdatingEdgesRef.current = false
              console.log('✅ [ProjectStudio] edges更新完成，允许样式更新')
            }, 500) // 增加延迟时间，确保React Flow完成渲染
          })
          
          return
        }
      } catch (error: any) {
        // 再次检查项目是否已切换
        if (loadingProjectIdRef.current !== currentProjectId) {
          console.log('⚠️ [ProjectStudio] 项目已切换，取消错误处理')
          return
        }
        
        // 处理不同类型的错误
        const status = error?.response?.status
        if (status === 404) {
          // 后端没有flow配置，这是正常情况（新项目）
          console.log('ℹ️ [ProjectStudio] 后端没有flow配置，使用默认节点')
        } else if (status === 401 || status === 403) {
          // 认证/授权错误
          console.error('❌ [ProjectStudio] 认证失败，请重新登录')
          showError('认证失败，请重新登录')
          setIsLoadingFlow(false)
          isLoadingFlowRef.current = false
          return
        } else if (status >= 500) {
          // 服务器错误
          console.error('❌ [ProjectStudio] 服务器错误:', error)
          showError('服务器错误，请稍后重试')
        } else {
          // 其他错误（网络错误、超时等）
          console.warn('⚠️ [ProjectStudio] 加载flow配置失败:', error)
          // 不显示错误提示，因为可能是网络问题，用户可以稍后重试
        }
      }
      
      // 再次检查项目是否已切换
      if (loadingProjectIdRef.current !== currentProjectId) {
        return
      }
      
      // 如果后端没有flow配置（404），说明这是新项目或flow配置还未保存
      // 优先使用本地store的配置（可能用户已经添加了节点和连线但未保存）
      // 如果本地store也没有配置，使用默认节点
      const defaultNodes = getDefaultNodes()
      const defaultEdges = getDefaultEdges()
      
      // 检查本地store是否有nodes和edges
      // 注意：即使nodes为空，如果edges不为空，也应该保留edges（用户可能删除了所有节点但保留了连线）
      const hasLocalNodes = project.nodes && project.nodes.length > 0
      const hasLocalEdges = project.edges && project.edges.length > 0
      
      // 优先使用本地store的配置（保留用户未保存的更改）
      const rawLocalNodes = hasLocalNodes ? project.nodes! : defaultNodes
      const localEdgesRaw = hasLocalEdges ? project.edges! : defaultEdges
      
      // 规范化节点（position 数字化 + 默认值合并）
      const localNodes = normalizeNodes(rawLocalNodes).map(applyNodeDefaults)
      
      // 确保edges有id字段（React Flow要求）
      const localEdges = localEdgesRaw.map((edge: Edge, index: number) => {
        // 规范化样式：确保strokeWidth是数字类型（React Flow要求）
        const normalizedStyle = edge.style 
          ? (typeof edge.style === 'string' 
              ? { stroke: "#94a3b8", strokeWidth: 2 } 
              : {
                  ...edge.style,
                  strokeWidth: typeof edge.style.strokeWidth === 'string' 
                    ? parseInt(edge.style.strokeWidth, 10) 
                    : (edge.style.strokeWidth || 2),
                  stroke: edge.style.stroke || "#94a3b8",
                })
          : { stroke: "#94a3b8", strokeWidth: 2 }
        
        if (!edge.id) {
          return {
            ...edge,
            id: `edge-${edge.source}-${edge.target}-${index}`,
            markerEnd: edge.markerEnd || { type: MarkerType.ArrowClosed, color: '#94a3b8' },
            style: normalizedStyle,
          }
        }
        return {
          ...edge,
          markerEnd: edge.markerEnd || { type: MarkerType.ArrowClosed, color: '#94a3b8' },
          style: normalizedStyle,
        }
      })
      
      console.log('ℹ️ [ProjectStudio] 使用本地store配置:', {
        hasLocalNodes,
        hasLocalEdges,
        nodesCount: localNodes.length,
        edgesCount: localEdges.length,
        edges: JSON.parse(JSON.stringify(localEdges.map((e: Edge) => ({ 
          id: e.id, 
          source: e.source, 
          target: e.target,
          sourceHandle: e.sourceHandle,
          markerEnd: e.markerEnd,
          style: e.style,
        })))),
        fullEdges: JSON.parse(JSON.stringify(localEdges)),
      })
      
      // 标记正在更新edges，防止visitedNodeIds effect干扰
      isUpdatingEdgesRef.current = true
      
      console.log('🔄 [ProjectStudio] 开始设置本地nodes和edges到React Flow状态:', {
        nodesCount: localNodes.length,
        edgesCount: localEdges.length,
        edgesIds: localEdges.map((e: Edge) => e.id),
      })
      
      // 使用 requestAnimationFrame 确保在下一个渲染周期设置，避免与visitedNodeIds effect冲突
      requestAnimationFrame(() => {
        setNodes(localNodes)
        setEdges(localEdges)
        
        console.log('✅ [ProjectStudio] 已设置本地nodes和edges到React Flow状态')
        
        // 延迟重置标记，确保React Flow完成渲染
        setTimeout(() => {
          isUpdatingEdgesRef.current = false
          console.log('✅ [ProjectStudio] 本地edges更新完成，允许样式更新')
        }, 500) // 增加延迟时间，确保React Flow完成渲染
      })
      
      // 如果本地store也没有nodes和edges，说明这是新项目，需要保存默认配置到后端
      // 但需要检查是否已经在Dashboard创建时保存过了（避免重复保存）
      if (!hasLocalNodes && !hasLocalEdges) {
        try {
          console.log('💾 [ProjectStudio] 保存默认flow配置到后端')
          const saveResponse = await FlowApiService.saveFlowConfig(currentProjectId, {
            nodes: defaultNodes,
            edges: defaultEdges,
            change_summary: '初始化默认流程配置',
          })
          
          // 再次检查项目是否已切换
          if (loadingProjectIdRef.current !== currentProjectId) {
            return
          }
          
          if (saveResponse.success) {
            // 更新本地store
            updateProjectNodes(currentProjectId, defaultNodes, defaultEdges)
            console.log('✅ [ProjectStudio] 默认flow配置已保存到后端')
          } else {
            console.warn('⚠️ [ProjectStudio] 保存默认flow配置失败:', saveResponse)
          }
        } catch (error: any) {
          // 再次检查项目是否已切换
          if (loadingProjectIdRef.current !== currentProjectId) {
            return
          }
          
          const status = error?.response?.status
          if (status === 409) {
            // 409 Conflict - 可能已经存在配置，忽略错误
            console.log('ℹ️ [ProjectStudio] Flow配置已存在，跳过保存')
          } else if (status === 401 || status === 403) {
            console.error('❌ [ProjectStudio] 认证失败，无法保存flow配置')
            showError('认证失败，请重新登录')
          } else {
            console.warn('⚠️ [ProjectStudio] 保存默认flow配置失败:', error)
            // 不显示错误提示，因为可能是网络问题，用户可以稍后手动保存
          }
        }
      }
      
      setIsSaved(true)
      hasChangesRef.current = false
      setIsLoadingFlow(false)
      isLoadingFlowRef.current = false
    }
    
    loadFlowConfig()
    
    // 清理函数：如果组件卸载或项目切换，重置加载状态
    return () => {
      if (loadingProjectIdRef.current !== effectiveProjectId) {
        isLoadingFlowRef.current = false
        setIsLoadingFlow(false)
      }
    }
  }, [effectiveProjectId, project?.id, setNodes, setEdges, updateProjectNodes, showError])
  
  // 根据执行状态更新边的样式（动画效果）
  // 边的三种状态：
  // 1. 未访问：灰色实线
  // 2. 正在传输（source已访问，target是当前活动节点）：紫色虚线动画
  // 3. 传输完成（source和target都已访问）：紫色实线高亮
  useEffect(() => {
    // 如果正在加载flow配置，跳过样式更新
    if (isLoadingFlow || isUpdatingEdgesRef.current || isLoadingFlowRef.current) {
      console.log('⏸️ [ProjectStudio] edge style effect: 正在加载flow配置，跳过样式更新', {
        isLoadingFlow,
        isUpdatingEdgesRef: isUpdatingEdgesRef.current,
        isLoadingFlowRef: isLoadingFlowRef.current,
      })
      return
    }
    
    // 使用函数式更新，确保基于最新的edges状态
    setEdges(eds => {
      // 如果edges为空，直接返回（避免在加载时清空edges）
      if (!eds || eds.length === 0) {
        console.log('⚠️ [ProjectStudio] edge style effect: edges为空，跳过更新', {
          edgesValue: eds,
          edgesLength: eds?.length,
        })
        return eds
      }
      
      console.log('🔄 [ProjectStudio] edge style effect执行:', {
        activeNodeId,
        visitedNodeIdsSize: visitedNodeIds.size,
        visitedNodeIds: Array.from(visitedNodeIds),
        edgesCount: eds.length,
      })
      
      if (visitedNodeIds.size === 0 && !activeNodeId) {
        // 重置所有边的样式（但保留edges本身）
        console.log('🔄 [ProjectStudio] edge style effect: 重置edges样式，edgesCount:', eds.length)
        const updatedEdges = eds.map(e => ({
          ...e,
          animated: false,
          style: { stroke: "#94a3b8", strokeWidth: 2 },
        }))
        return updatedEdges
      }
      
      // 根据执行状态更新边的样式
      console.log('🎨 [ProjectStudio] edge style effect: 更新edges样式')
      const updatedEdges = eds.map(e => {
        const sourceVisited = visitedNodeIds.has(e.source)
        const targetVisited = visitedNodeIds.has(e.target)
        
        // 正在传输：source已访问，target是当前活动节点
        const isTransiting = sourceVisited && e.target === activeNodeId
        // 传输完成：source和target都已访问
        const isCompleted = sourceVisited && targetVisited
        
        // 计算边的状态
        const shouldHighlight = isTransiting || isCompleted
        const shouldAnimate = isTransiting  // 只有正在传输时才显示虚线动画
        
        console.log(`  边 ${e.source} -> ${e.target}: transiting=${isTransiting}, completed=${isCompleted}, animate=${shouldAnimate}`)
        
        return {
          ...e,
          animated: shouldAnimate,
          style: shouldHighlight 
            ? { stroke: "#8b5cf6", strokeWidth: 3 }  // 紫色高亮
            : { stroke: "#94a3b8", strokeWidth: 2 },  // 灰色默认
        }
      })
      
      return updatedEdges
    })
  }, [activeNodeId, visitedNodeIds, setEdges, isLoadingFlow])
  
  // 调试日志：检查React Flow接收到的edges
  useEffect(() => {
    console.log('🎨 [ProjectStudio] ReactFlow渲染时的edges状态:', {
      edgesCount: edges.length,
      edges: JSON.parse(JSON.stringify(edges.map(e => ({ 
        id: e.id, 
        source: e.source, 
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        hasMarkerEnd: !!e.markerEnd,
        hasStyle: !!e.style,
        markerEnd: e.markerEnd,
        style: e.style,
      })))),
      fullEdges: JSON.parse(JSON.stringify(edges)),
    })
  }, [edges])

  // 处理连线
  const onConnect = useCallback(
    (params: Connection) => {
      console.log('🔗 [ProjectStudio] 用户创建连线:', {
        source: params.source,
        target: params.target,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        params,
      })
      
      // 检查源节点是否是Binder节点，如果是，添加默认边数据
      const sourceNode = nodes.find(n => n.id === params.source)
      const isFromBinder = sourceNode?.type === 'binder'
      
      // v13.0: 为从 Binder 出发的边计算初始顺序
      let binderEdgeData = undefined
      if (isFromBinder) {
        // 计算当前从该 Binder 出发的边数量，用作新边的顺序
        const existingEdges = edges.filter(e => e.source === params.source)
        const nextOrder = existingEdges.length
        
        binderEdgeData = {
          silent: false,
          is_primary: false,
          order: nextOrder,
        }
      }
      
      const newEdge = addEdge({
        ...params,
        markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
        style: { stroke: "#94a3b8", strokeWidth: 2 },
        data: binderEdgeData,
      }, edges)
      
      console.log('🔗 [ProjectStudio] 连线后的edges:', {
        edgesCount: newEdge.length,
        edges: JSON.parse(JSON.stringify(newEdge)),
        lastEdge: JSON.parse(JSON.stringify(newEdge[newEdge.length - 1])),
      })
      
      setEdges(newEdge)
      
      // 当从 Router 节点拖出连线时，同步更新 Router 条件的 target（双向同步）
      if (params.source && params.sourceHandle && params.target) {
        setNodes((nds) => nds.map((n) => {
          if (n.id === params.source && n.type === 'router') {
            const currentConditions = n.data.conditions || []
            const conditionExists = currentConditions.some((c: Condition) => c.id === params.sourceHandle)
            
            if (conditionExists) {
              console.log('🔗 [onConnect] 同步更新 Router 条件 target:', {
                routerId: params.source,
                conditionId: params.sourceHandle,
                newTarget: params.target,
              })
              return {
                ...n,
                data: {
                  ...n.data,
                  conditions: currentConditions.map((c: Condition) => 
                    c.id === params.sourceHandle ? { ...c, target: params.target } : c
                  ),
                },
              }
            }
          }
          return n
        }))
      }
      
      hasChangesRef.current = true
      setIsSaved(false)
      
      console.log('✅ [ProjectStudio] 连线已添加到状态')
    },
    [setEdges, edges, setNodes]
  )

  // 处理节点点击
  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNodeId(node.id)
    setSelectedEdgeId(null)  // 清除边选择
    setRightPanel('config')
  }, [])

  // 处理边点击
  const onEdgeClick = useCallback((_: React.MouseEvent, edge: Edge) => {
    setSelectedEdgeId(edge.id)
    setSelectedNodeId(null)  // 清除节点选择
    setRightPanel('config')
  }, [])

  // 处理画布点击
  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null)
    setSelectedEdgeId(null)
    if (rightPanel === 'config') {
      setRightPanel('none')
    }
  }, [rightPanel])

  // 自定义 onNodesChange - 标记有更改
  const handleNodesChange = useCallback((changes: Parameters<typeof onNodesChange>[0]) => {
    onNodesChange(changes)
    // 只在位置变化时标记
    const hasPositionChange = changes.some(c => c.type === 'position' && c.dragging === false)
    if (hasPositionChange) {
      hasChangesRef.current = true
      setIsSaved(false)
    }
  }, [onNodesChange])

  // 自定义 onEdgesChange - 标记有更改，并同步更新 Router 条件
  const handleEdgesChange = useCallback((changes: Parameters<typeof onEdgesChange>[0]) => {
    console.log('🔄 [ProjectStudio] edges变化:', {
      changesCount: changes.length,
      changes: changes,
      currentEdgesCount: edges.length,
    })
    
    // 处理边删除时，同步清空 Router 条件的 target（双向同步）
    const removeChanges = changes.filter(c => c.type === 'remove')
    if (removeChanges.length > 0) {
      // 找到被删除的边
      const removedEdgeIds = removeChanges.map(c => c.id)
      const removedEdges = edges.filter(e => removedEdgeIds.includes(e.id))
      
      // 对于每个被删除的边，如果是从 Router 出发的，清空对应条件的 target
      removedEdges.forEach(edge => {
        if (edge.sourceHandle) {
          console.log('🔗 [handleEdgesChange] 边被删除，同步清空 Router 条件 target:', {
            routerId: edge.source,
            conditionId: edge.sourceHandle,
          })
          setNodes((nds) => nds.map((n) => {
            if (n.id === edge.source && n.type === 'router') {
              const currentConditions = n.data.conditions || []
              return {
                ...n,
                data: {
                  ...n.data,
                  conditions: currentConditions.map((c: Condition) => 
                    c.id === edge.sourceHandle ? { ...c, target: '' } : c
                  ),
                },
              }
            }
            return n
          }))
        }
      })
    }
    
    onEdgesChange(changes)
    
    if (changes.length > 0) {
      console.log('📝 [ProjectStudio] 检测到edges变化，标记为未保存')
      hasChangesRef.current = true
      setIsSaved(false)
    }
  }, [onEdgesChange, edges, setNodes])

  // 添加新 Agent 节点
  const addAgentNode = useCallback(() => {
    const newNode: Node = {
      id: `agent-${Date.now()}`,
      type: "agent",
      position: { x: 350 + Math.random() * 50, y: 150 + Math.random() * 100 },
      data: { ...NODE_TYPE_DEFAULTS.agent },
    }
    setNodes((nds) => [...nds, newNode])
    hasChangesRef.current = true
    setIsSaved(false)
  }, [setNodes])

  // v10.0 新增：添加 PersonaAgent 节点（身份模拟专用）
  const addPersonaAgentNode = useCallback(() => {
    const newNode: Node = {
      id: `persona-agent-${Date.now()}`,
      type: "persona_agent",
      position: { x: 350 + Math.random() * 50, y: 150 + Math.random() * 100 },
      data: { ...NODE_TYPE_DEFAULTS.persona_agent },
    }
    setNodes((nds) => [...nds, newNode])
    hasChangesRef.current = true
    setIsSaved(false)
  }, [setNodes])

  // v11.3 新增：添加 Structured Agent 节点（真正的 Agent Loop）
  const addStructuredAgentNode = useCallback(() => {
    const newNode: Node = {
      id: `structured-agent-${Date.now()}`,
      type: "structured_agent",
      position: { x: 350 + Math.random() * 50, y: 150 + Math.random() * 100 },
      data: { ...NODE_TYPE_DEFAULTS.structured_agent },
    }
    setNodes((nds) => [...nds, newNode])
    hasChangesRef.current = true
    setIsSaved(false)
  }, [setNodes])

  // v12.0 新增：添加 Companion Agent 节点（陪伴智能体）
  const addCompanionAgentNode = useCallback(() => {
    const newNode: Node = {
      id: `companion-agent-${Date.now()}`,
      type: "companion_agent",
      position: { x: 350 + Math.random() * 50, y: 150 + Math.random() * 100 },
      data: { ...NODE_TYPE_DEFAULTS.companion_agent },
    }
    setNodes((nds) => [...nds, newNode])
    hasChangesRef.current = true
    setIsSaved(false)
  }, [setNodes])

  // v16.0 新增：添加 HermesAgent 节点（原生智能体）
  const addHermesAgentNode = useCallback(() => {
    const newNode: Node = {
      id: `hermes-agent-${Date.now()}`,
      type: "hermes_agent",
      position: { x: 350 + Math.random() * 50, y: 150 + Math.random() * 100 },
      data: { ...NODE_TYPE_DEFAULTS.hermes_agent },
    }
    setNodes((nds) => [...nds, newNode])
    hasChangesRef.current = true
    setIsSaved(false)
  }, [setNodes])

  // v13.0 新增：添加 Binder 节点（并行分支编排器）
  const addBinderNode = useCallback(() => {
    const newNode: Node = {
      id: `binder-${Date.now()}`,
      type: "binder",
      position: { x: 350 + Math.random() * 50, y: 150 + Math.random() * 100 },
      data: { ...NODE_TYPE_DEFAULTS.binder },
    }
    setNodes((nds) => [...nds, newNode])
    hasChangesRef.current = true
    setIsSaved(false)
  }, [setNodes])

  // v13.0: 调整 Binder 节点分支的执行顺序
  const moveEdgeOrder = useCallback((sourceNodeId: string, currentIndex: number, direction: 'up' | 'down') => {
    if (currentIndex === -1) return
    
    const outgoingEdges = edges.filter(edge => edge.source === sourceNodeId)
    const sortedEdges = outgoingEdges.sort((a, b) => (a.data?.order ?? 0) - (b.data?.order ?? 0))
    
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    
    if (targetIndex < 0 || targetIndex >= sortedEdges.length) return
    
    // 交换两个边的顺序
    const currentEdge = sortedEdges[currentIndex]
    const targetEdge = sortedEdges[targetIndex]
    
    setEdges((eds) => eds.map(edge => {
      if (edge.id === currentEdge.id) {
        return { ...edge, data: { ...edge.data, order: targetEdge.data?.order ?? 0 } }
      }
      if (edge.id === targetEdge.id) {
        return { ...edge, data: { ...edge.data, order: currentEdge.data?.order ?? 0 } }
      }
      return edge
    }))
    
    hasChangesRef.current = true
    setIsSaved(false)
  }, [edges, setEdges])

  // 生成唯一的条件 ID
  // 格式：{projectId}-c-{timestamp}-{随机字符串}
  // 包含 projectId 便于调试和追踪，复制项目时 ID 会自动变化
  const generateConditionId = useCallback(() => {
    const timestamp = Date.now()
    const random = Math.random().toString(36).substring(2, 6) // 4位随机字符
    // effectiveProjectId 在项目创建时就已存在（创建时即存储到数据库）
    const projectPrefix = effectiveProjectId || 'local'
    return `${projectPrefix}-c-${timestamp}-${random}`
  }, [effectiveProjectId])

  // 添加路由节点
  const addRouterNode = useCallback(() => {
    const timestamp = Date.now()
    const newNode: Node = {
      id: `router-${timestamp}`,
      type: "router",
      position: { x: 350 + Math.random() * 50, y: 150 + Math.random() * 100 },
      data: { 
        label: "条件判断",
        conditions: [
          { id: generateConditionId(), name: "条件 1", condition: "", target: "" },
          { id: generateConditionId(), name: "条件 2", condition: "", target: "" },
        ]
      },
    }
    setNodes((nds) => [...nds, newNode])
    hasChangesRef.current = true
    setIsSaved(false)
  }, [setNodes, generateConditionId])

  // 更新节点数据
  const updateNodeData = useCallback((field: string, value: unknown) => {
    if (!selectedNodeId) return
    
    setNodes((nds) => nds.map((n) => {
      if (n.id === selectedNodeId) {
        // 支持嵌套字段，如 'user_prompt_templates.user' 或 'conditions.0.condition'
        if (field.includes('.')) {
          const fieldParts = field.split('.')
          
          // 深度克隆并更新嵌套数据
          const updateNestedData = (data: any, parts: string[], newValue: unknown): any => {
            if (parts.length === 1) {
              return { ...data, [parts[0]]: newValue }
            }
            
            const [currentPart, ...remainingParts] = parts
            const isArrayIndex = /^\d+$/.test(currentPart)
            
            if (isArrayIndex) {
              // 处理数组索引
              const index = parseInt(currentPart, 10)
              const currentArray = Array.isArray(data) ? [...data] : []
              
              // 确保数组有足够的长度
              while (currentArray.length <= index) {
                currentArray.push({})
              }
              
              currentArray[index] = updateNestedData(currentArray[index], remainingParts, newValue)
              return currentArray
            } else {
              // 处理对象属性
              return {
                ...data,
                [currentPart]: updateNestedData(data[currentPart] || {}, remainingParts, newValue)
              }
            }
          }
          
          return {
            ...n,
            data: updateNestedData(n.data, fieldParts, value)
          }
        } else {
          return {
            ...n,
            data: {
              ...n.data,
              [field]: value,
            },
          }
        }
      }
      return n
    }))
    
    // 🔗 特殊处理：路由节点条件target变化时自动创建/更新连线
    if (selectedNodeId && field === 'conditions') {
      const selectedNode = nodes.find(n => n.id === selectedNodeId)
      if (selectedNode?.type === 'router' && Array.isArray(value)) {
        const newConditions = value as Condition[]
        
        // 为每个有target的条件创建边
        newConditions.forEach((condition) => {
          if (condition.target && condition.id) {
            const edgeId = `reactflow__edge-${selectedNodeId}-${condition.target}`
            const existingEdge = edges.find(e => e.id === edgeId)
            
            if (!existingEdge) {
              // 创建新边
              const newEdge = {
                id: edgeId,
                source: selectedNodeId,
                target: condition.target,
                sourceHandle: condition.id, // 使用条件ID作为sourceHandle
                targetHandle: null,
                type: 'default',
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: '#94a3b8',
                },
                style: {
                  strokeWidth: 2,
                  stroke: '#94a3b8',
                },
              }
              
              console.log('🔗 [updateNodeData] 自动创建路由连线:', {
                routerId: selectedNodeId,
                conditionId: condition.id,
                targetId: condition.target,
                edgeId: edgeId,
              })
              
              setEdges(prev => [...prev, newEdge])
            }
          }
        })
        
        // 删除不再需要的边（条件被删除或target被清空）
        const validConditionIds = new Set(newConditions.filter(c => c.target).map(c => c.id))
        setEdges(prev => prev.filter(edge => {
          if (edge.source === selectedNodeId && edge.sourceHandle) {
            const shouldKeep = validConditionIds.has(edge.sourceHandle)
            if (!shouldKeep) {
              console.log('🗑️ [updateNodeData] 删除无效的路由连线:', {
                edgeId: edge.id,
                conditionId: edge.sourceHandle,
              })
            }
            return shouldKeep
          }
          return true
        }))
      }
    }
    
    hasChangesRef.current = true
    setIsSaved(false)
  }, [selectedNodeId, setNodes, nodes, edges, setEdges])

  // 打开提示词编辑器 (占位函数)
  const onOpenPromptEditor = useCallback((field: string, content: string, title: string, description: string) => {
    setPromptEditorField(field)
    setPromptEditorContent(content)
    setPromptEditorTitle(title)
    setPromptEditorDescription(description)
    setPromptEditorLanguage('markdown')  // 默认使用 markdown
    setPromptEditorOpen(true)
  }, [])



  // 更新边数据
  const updateEdgeData = useCallback((field: string, value: unknown) => {
    if (!selectedEdgeId) return
    
    setEdges((eds) => eds.map((e) => {
      if (e.id === selectedEdgeId) {
        return {
          ...e,
          data: {
            ...e.data,
            [field]: value,
          },
        }
      }
      return e
    }))
    
    hasChangesRef.current = true
    setIsSaved(false)
  }, [selectedEdgeId, setEdges])

  // 设置主分支（确保唯一性）
  const setPrimaryBranch = useCallback((edgeId: string, isPrimary: boolean) => {
    if (!isPrimary) {
      // 如果取消主分支，直接更新
      setEdges((eds) => eds.map((e) => {
        if (e.id === edgeId) {
          return {
            ...e,
            data: {
              ...e.data,
              is_primary: false,
            },
          }
        }
        return e
      }))
    } else {
      // 如果设置为主分支，需要确保同一个源节点的其他边不是主分支
      const targetEdge = edges.find(e => e.id === edgeId)
      if (!targetEdge) return
      
      setEdges((eds) => eds.map((e) => {
        if (e.id === edgeId) {
          // 设置当前边为主分支
          return {
            ...e,
            data: {
              ...e.data,
              is_primary: true,
            },
          }
        } else if (e.source === targetEdge.source) {
          // 取消同一源节点其他边的主分支状态
          return {
            ...e,
            data: {
              ...e.data,
              is_primary: false,
            },
          }
        }
        return e
      }))
    }
    
    hasChangesRef.current = true
    setIsSaved(false)
  }, [edges, setEdges])


  // 关闭配置面板
  const closeConfigPanel = useCallback(() => {
    setSelectedNodeId(null)
    setRightPanel('none')
  }, [])

  // 关闭测试面板
  const closeTestPanel = useCallback(() => {
    setRightPanel('none')
  }, [])

  // 保存项目
  const handleSave = useCallback(async () => {
    const projectIdToSave = currentProjectId || id
    if (!projectIdToSave) return
    
    setIsSaving(true)
    setIsSaved(false)
    
    let newProjectId: string | null = null // 用于存储新创建的项目ID
    
    try {
      console.log('💾 [ProjectStudio] 开始保存项目:', { 
        projectId: projectIdToSave, 
        nodesCount: nodes.length, 
        edgesCount: edges.length,
        nodes: JSON.parse(JSON.stringify(nodes.map(n => ({ id: n.id, type: n.type, position: n.position })))),
        edges: JSON.parse(JSON.stringify(edges.map(e => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, markerEnd: e.markerEnd, style: e.style })))),
        fullEdges: JSON.parse(JSON.stringify(edges)),
      })
      
      // 调用后端API保存流程配置
      const response = await FlowApiService.saveFlowConfig(projectIdToSave, {
        nodes,
        edges,
        change_summary: undefined, // 可以后续添加变更摘要功能
      })
      
      console.log('💾 [ProjectStudio] 保存API响应:', {
        success: response.success,
        data: response.data,
      })
      
      if (response.success && response.data) {
        console.log('✅ [ProjectStudio] 项目保存成功:', {
          version: response.data.version,
          updated_at: response.data.updated_at,
          validation: response.data.validation,
        })
        
        console.log('💾 [ProjectStudio] 更新本地store:', {
          projectId: projectIdToSave,
          nodesCount: nodes.length,
          edgesCount: edges.length,
          edges: JSON.parse(JSON.stringify(edges.map(e => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, markerEnd: e.markerEnd, style: e.style })))),
          fullEdges: JSON.parse(JSON.stringify(edges)),
        })
        
        // 保存成功后，更新本地状态
        updateProjectNodes(projectIdToSave, nodes, edges)
        
        console.log('✅ [ProjectStudio] 本地store已更新')
        
        hasChangesRef.current = false
        setIsSaved(true)
        setShowSaveSuccess(true)
        setTimeout(() => setShowSaveSuccess(false), 2000)
        
        // 显示成功提示
        showSuccess('项目保存成功', '保存')
        
        // 如果有验证警告，显示警告信息
        if (response.data.validation && response.data.validation.warnings.length > 0) {
          const warnings = response.data.validation.warnings.map(w => w.message).join('; ')
          console.warn('⚠️ [ProjectStudio] 保存成功但有警告:', warnings)
        }
      } else {
        throw new Error('保存失败：响应格式错误')
      }
    } catch (error: any) {
      console.error('❌ [ProjectStudio] 项目保存失败:', error)
      console.error('❌ [ProjectStudio] 错误详情:', {
        message: error?.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data,
        isAxiosError: error?.isAxiosError,
      })
      
      // 处理不同类型的错误
      let errorMessage = '保存失败，请稍后重试'
      
      const status = error?.response?.status
      const errorData = error?.response?.data
      
      if (status === 404) {
        // 项目不存在，尝试创建项目
        if (errorData?.error?.code === 'PROJ_002' || errorData?.error?.message?.includes('项目不存在') || error?.message?.includes('项目不存在')) {
          console.log('⚠️ [ProjectStudio] 项目不存在，尝试创建项目...')
          
          try {
            // 从本地状态获取项目信息
            const localProject = projects.find(p => p.id === projectIdToSave)
            if (!localProject) {
              throw new Error('本地项目信息不存在，无法创建项目')
            }
            
            // 创建项目
            // 确保必填字段有效（后端要求name和company的min_length=2）
            const projectName = localProject.name || `项目_${projectIdToSave.slice(-8)}`
            const projectCompany = localProject.company && localProject.company.length >= 2 
              ? localProject.company 
              : '默认公司'
            
            console.log('📝 [ProjectStudio] 创建项目参数:', {
              name: projectName,
              company: projectCompany,
              description: localProject.description || '',
            })
            
            const createResponse = await ProjectApiService.createProject({
              name: projectName,
              company: projectCompany,
              description: localProject.description || '',
            })
            
            console.log('✅ [ProjectStudio] 项目创建成功:', createResponse.data)
            
            // 使用创建项目返回的新项目ID来保存流程配置
            newProjectId = createResponse.data?.id || null
            if (!newProjectId) {
              throw new Error('创建项目成功但未返回项目ID')
            }
            
            // 项目创建成功后，使用新的项目ID保存流程配置
            const saveResponse = await FlowApiService.saveFlowConfig(newProjectId, {
              nodes,
              edges,
              change_summary: undefined,
            })
            
            if (saveResponse.success && saveResponse.data) {
              // 检查新项目是否已经在本地状态中
              const existingProject = projects.find(p => p.id === newProjectId)
              if (!existingProject) {
                // 如果项目不存在，需要添加到本地状态
                const newProject: AgentProject = {
                  id: newProjectId,
                  name: projectName,
                  company: projectCompany,
                  description: localProject.description || '',
                  status: ProjectStatus.DRAFT,
                  api_key: createResponse.data?.api_key || `sk_${newProjectId}`,
                  api_key_created_at: new Date().toISOString(),
                  owner_id: localProject.owner_id || 'usr_001', // TODO: 从 authStore 获取
                  current_version: saveResponse.data.version || 1,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  nodes: nodes,
                  edges: edges,
                }
                // 添加新项目到本地状态
                addProject(newProject)
              } else {
                // 如果项目已存在，更新项目信息
                updateProject(newProjectId, {
                  name: projectName,
                  company: projectCompany,
                  description: localProject.description || '',
                  api_key: createResponse.data?.api_key || existingProject.api_key,
                })
              }
              
              // 保存成功后，更新本地状态（使用新的项目ID）
              updateProjectNodes(newProjectId, nodes, edges)
              hasChangesRef.current = false
              setIsSaved(true)
              setShowSaveSuccess(true)
              setTimeout(() => setShowSaveSuccess(false), 2000)
              
              showSuccess('项目创建并保存成功', '保存')
              
              // 立即更新当前项目ID state，确保TestPanel使用新的项目ID
              setCurrentProjectId(newProjectId)
              
              // 如果创建的项目ID和当前URL中的ID不一致，需要更新URL
              if (newProjectId !== projectIdToSave) {
                console.log(`⚠️ [ProjectStudio] 项目ID不一致，更新URL: ${projectIdToSave} -> ${newProjectId}`)
                // 使用 requestAnimationFrame 确保DOM更新完成后再更新URL
                requestAnimationFrame(() => {
                  navigate(`/project/${newProjectId}`, { replace: true })
                })
              }
              
              return
            } else {
              throw new Error('流程配置保存失败')
            }
          } catch (createError: any) {
            console.error('❌ [ProjectStudio] 创建项目失败:', createError)
            errorMessage = createError?.response?.data?.error?.message || createError?.message || '创建项目失败，请检查项目信息'
          }
        } else {
          errorMessage = errorData?.error?.message || error?.message || '项目不存在'
        }
      } else if (status === 401) {
        // 401 认证失败
        if (errorData?.error?.message) {
          errorMessage = errorData.error.message
        } else if (errorData?.error?.code === 'AUTH_007' || errorData?.error?.code === 'AUTH_008') {
          errorMessage = '登录已过期，请重新登录'
        } else {
          errorMessage = '认证失败，请重新登录'
        }
      } else if (status === 403) {
        errorMessage = '没有权限保存此项目'
      } else if (status === 422) {
        // 验证错误
        if (errorData?.error?.details?.errors) {
          const validationErrors = errorData.error.details.errors
            .map((e: any) => e.message)
            .join('; ')
          errorMessage = `流程配置验证失败：${validationErrors}`
        } else {
          errorMessage = errorData?.error?.message || '流程配置验证失败'
        }
      } else if (status === 500) {
        // 服务器内部错误
        errorMessage = errorData?.error?.message || errorData?.detail || '服务器内部错误，请检查后端日志或联系管理员'
        console.error('❌ [ProjectStudio] 服务器内部错误:', {
          status: 500,
          errorData,
          errorMessage,
        })
      } else if (error?.message) {
        // 检查错误消息中是否包含有用的信息
        if (error.message.includes('项目不存在') || error.message.includes('PROJ_002')) {
          // 如果错误消息提到项目不存在，尝试创建项目
          console.log('⚠️ [ProjectStudio] 从错误消息检测到项目不存在，尝试创建项目...')
          
          try {
            const localProject = projects.find(p => p.id === projectIdToSave)
            if (!localProject) {
              throw new Error('本地项目信息不存在，无法创建项目')
            }
            
            // 确保必填字段有效（后端要求name和company的min_length=2）
            const projectNameFromError = localProject.name || `项目_${projectIdToSave.slice(-8)}`
            const projectCompanyFromError = localProject.company && localProject.company.length >= 2 
              ? localProject.company 
              : '默认公司'
            
            const createResponse = await ProjectApiService.createProject({
              name: projectNameFromError,
              company: projectCompanyFromError,
              description: localProject.description || '',
            })
            
            console.log('✅ [ProjectStudio] 项目创建成功:', createResponse.data)
            
            // 使用创建项目返回的新项目ID来保存流程配置
            const newProjectIdFromError = createResponse.data?.id
            if (!newProjectIdFromError) {
              throw new Error('创建项目成功但未返回项目ID')
            }
            
            // 检查新项目是否已经在本地状态中
            const existingProjectFromError = projects.find(p => p.id === newProjectIdFromError)
            if (!existingProjectFromError) {
              // 如果项目不存在，需要添加到本地状态
              const newProjectFromError: AgentProject = {
                id: newProjectIdFromError,
                name: projectNameFromError,
                company: projectCompanyFromError,
                description: localProject.description || '',
                status: ProjectStatus.DRAFT,
                api_key: createResponse.data?.api_key || `sk_${newProjectIdFromError}`,
                api_key_created_at: new Date().toISOString(),
                owner_id: localProject.owner_id || 'usr_001', // TODO: 从 authStore 获取
                current_version: 1,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                nodes: nodes,
                edges: edges,
              }
              // 添加新项目到本地状态
              addProject(newProjectFromError)
            } else {
              // 如果项目已存在，更新项目信息
              updateProject(newProjectIdFromError, {
                name: projectNameFromError,
                company: projectCompanyFromError,
                description: localProject.description || '',
                api_key: createResponse.data?.api_key || existingProjectFromError.api_key,
              })
            }
            
            // 使用新的项目ID保存流程配置
            const saveResponse = await FlowApiService.saveFlowConfig(newProjectIdFromError, {
              nodes,
              edges,
              change_summary: undefined,
            })
            
            if (saveResponse.success && saveResponse.data) {
              // 保存成功后，更新本地状态（使用新的项目ID）
              updateProjectNodes(newProjectIdFromError, nodes, edges)
              hasChangesRef.current = false
              setIsSaved(true)
              setShowSaveSuccess(true)
              setTimeout(() => setShowSaveSuccess(false), 2000)
              
              showSuccess('项目创建并保存成功', '保存')
              
              // 立即更新当前项目ID state，确保TestPanel使用新的项目ID
              setCurrentProjectId(newProjectIdFromError)
              
              // 如果创建的项目ID和当前URL中的ID不一致，需要更新URL
              if (newProjectIdFromError !== projectIdToSave) {
                console.log(`⚠️ [ProjectStudio] 项目ID不一致，更新URL: ${projectIdToSave} -> ${newProjectIdFromError}`)
                // 使用 requestAnimationFrame 确保DOM更新完成后再更新URL
                requestAnimationFrame(() => {
                  navigate(`/project/${newProjectIdFromError}`, { replace: true })
                })
              }
              
              return
            } else {
              throw new Error('流程配置保存失败')
            }
          } catch (createError: any) {
            console.error('❌ [ProjectStudio] 创建项目失败:', createError)
            errorMessage = createError?.response?.data?.error?.message || createError?.message || '创建项目失败，请检查项目信息'
          }
        } else {
          errorMessage = error.message
        }
      }
      
      // 显示错误提示
      showError(errorMessage, '保存失败')
      setIsSaved(false)
    } finally {
      setIsSaving(false)
    }
  }, [id, nodes, edges, projects, updateProjectNodes, showSuccess, showError])

  // 测试运行 - 点击测试时自动先保存
  const handleTest = useCallback(async () => {
    setSelectedNodeId(null)
    
    // 如果已经打开测试面板，则关闭
    if (rightPanel === 'test') {
      setRightPanel('none')
      return
    }
    
    // 如果有未保存的修改，先自动保存
    if (!isSaved || hasChangesRef.current) {
      console.log('📝 [ProjectStudio] 有未保存的修改，先自动保存...')
      await handleSave()
    }
    
    // 打开测试面板
    setRightPanel('test')
  }, [rightPanel, isSaved, handleSave])

  // 发布
  const handlePublish = useCallback(async () => {
    const projectIdToPublish = currentProjectId || id
    if (!projectIdToPublish) {
      showError('请先保存项目')
      return
    }
    
    // 检查是否有未保存的更改
    if (!isSaved || hasChangesRef.current) {
      showError('请先保存更改后再发布')
      return
    }
    
    // 检查是否有节点
    if (!nodes || nodes.length === 0) {
      showError('请先创建流程节点')
      return
    }
    
    try {
      setIsPublishing(true)
      console.log('🚀 [ProjectStudio] 开始发布项目:', projectIdToPublish)
      
      const response = await FlowApiService.publishFlow(projectIdToPublish)
      
      if (response.success) {
        console.log('✅ [ProjectStudio] 发布成功:', response.data)
        setIsPublished(true)
        setShowPublishSuccess(true)
        showSuccess('发布成功！项目已上线，可在运行时监控中查看')
        
        // 3秒后隐藏成功状态
        setTimeout(() => {
          setShowPublishSuccess(false)
        }, 3000)
      } else {
        throw new Error('发布失败')
      }
    } catch (error) {
      console.error('❌ [ProjectStudio] 发布失败:', error)
      const errorMessage = error instanceof Error ? error.message : '发布失败，请重试'
      showError(errorMessage)
    } finally {
      setIsPublishing(false)
    }
  }, [currentProjectId, id, isSaved, nodes, showError, showSuccess])

  // 执行状态 Context 值（必须在 early return 之前调用，遵守 hooks 规则）
  const executionContextValue = useMemo(() => {
    console.log('🔄 [ProjectStudio] executionContextValue 更新:', JSON.stringify({
      activeNodeId,
      visitedNodeIds: Array.from(visitedNodeIds),
      sceneConfigsCount: sceneConfigs.length,
    }))
    return {
      activeNodeId,
      visitedNodeIds,
      sceneConfigs,  // v10.3: 场景配置列表
    }
  }, [activeNodeId, visitedNodeIds, sceneConfigs])

  // 如果项目不存在（所有 hooks 必须在此之前调用）
  if (!project) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-100">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="text-center text-red-500">项目不存在</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-slate-500 mb-4">找不到 ID 为 {id} 的项目</p>
            <Link to="/">
              <Button>返回首页</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <ExecutionContext.Provider value={executionContextValue}>
    <div className="h-screen flex flex-col bg-slate-100 overflow-hidden">
      {/* Toast 容器 */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      
      {/* 顶部导航 */}
      <ProjectToolbar
        project={project}
        projectId={id || ''}
        rightPanel={rightPanel}
        setRightPanel={setRightPanel}
        memoryEnabled={memoryEnabled}
        isLoadingSettings={isLoadingSettings}
        isSavingSettings={isSavingSettings}
        onMemoryToggle={handleMemoryToggle}
        isSaved={isSaved}
        isSaving={isSaving}
        showSaveSuccess={showSaveSuccess}
        onSave={handleSave}
        isPublishing={isPublishing}
        showPublishSuccess={showPublishSuccess}
        isPublished={isPublished}
        nodesCount={nodes.length}
        onPublish={handlePublish}
        onTest={handleTest}
        onOpenSettings={handleOpenSettings}
      />

      {/* 主工作区 */}
      <div className="flex-1 flex overflow-hidden">
        {/* 画布区域 */}
        <div className="flex-1 relative">
          <ReactFlow
            key={effectiveProjectId} // 添加key，当projectId变化时强制重新渲染
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            nodeTypes={memoizedNodeTypes}
            defaultEdgeOptions={memoizedDefaultEdgeOptions}
            fitView
            snapToGrid
            snapGrid={memoizedSnapGrid}
            connectionLineStyle={memoizedConnectionLineStyle}
            connectionLineType={ConnectionLineType.SmoothStep}
            elevateNodesOnSelect={true}
            nodesDraggable={true}
            nodesConnectable={true}
            elementsSelectable={true}
            minZoom={0.3}
            maxZoom={2}
            nodeDragThreshold={2}
            selectNodesOnDrag={false}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#cbd5e1" />
            <Controls showInteractive={false} />
            
            {/* 节点工具栏 */}
            <NodeToolbar
              onAddAgentNode={addAgentNode}
              onAddStructuredAgentNode={addStructuredAgentNode}
              onAddCompanionAgentNode={addCompanionAgentNode}
              onAddPersonaAgentNode={addPersonaAgentNode}
              onAddRouterNode={addRouterNode}
              onAddBinderNode={addBinderNode}
              onAddHermesAgentNode={addHermesAgentNode}
            />

            {/* 提示信息 */}
            <Panel position="bottom-center" className="text-xs text-slate-500 bg-white/80 px-3 py-1.5 rounded-full shadow-sm">
              💡 从节点边缘的圆点拖出连线 | 点击节点/连线进行配置 | 滚轮缩放
            </Panel>
          </ReactFlow>
        </div>

        {/* 右侧配置面板 */}
        {rightPanel === 'config' && selectedNode && (
          <ConfigPanel
            selectedNode={selectedNode}
            nodes={nodes}
            edges={edges}
            projectId={effectiveProjectId || ''}
            modelsByProvider={modelsByProvider}
            defaultModel={defaultModel}
            tools={tools}
            builtinTools={builtinTools}
            isLoadingTools={isLoadingTools}
            userContextApiConfigured={!!userContextApi}
            outputNodes={nodes.filter(n => n.type === 'output')}
            onClose={closeConfigPanel}
            onUpdateNodeData={updateNodeData}
            onOpenPromptEditor={onOpenPromptEditor}
            onMoveEdgeOrder={moveEdgeOrder}
            promptEditorOpen={promptEditorOpen}
            promptEditorContent={promptEditorContent}
            promptEditorLanguage={promptEditorLanguage}
            promptEditorField={promptEditorField}
            promptEditorTitle={promptEditorTitle}
            promptEditorDescription={promptEditorDescription}
            setPromptEditorOpen={setPromptEditorOpen}
            setPromptEditorContent={setPromptEditorContent}
            setPromptEditorLanguage={setPromptEditorLanguage}
            setPromptEditorField={setPromptEditorField}
            setPromptEditorTitle={setPromptEditorTitle}
            setPromptEditorDescription={setPromptEditorDescription}
          />
        )}

        {/* 边配置面板 */}
        {rightPanel === 'config' && selectedEdgeId && !selectedNode && (
          <EdgeConfigPanel
            selectedEdge={edges.find(e => e.id === selectedEdgeId) || null}
            onClose={closeConfigPanel}
            onUpdateEdgeData={updateEdgeData}
            onSetPrimaryBranch={setPrimaryBranch}
            nodes={nodes}
            edges={edges}
          />
        )}

        {/* 右侧测试面板 */}
        {rightPanel === 'test' && (
          <TestPanel
            projectName={project?.name || '未命名项目'}
            projectId={effectiveProjectId || ''}
            isSaved={isSaved}  // 传递保存状态，未保存时禁止测试
            onClose={closeTestPanel}
            agents={agentNodes.map(node => ({
              id: node.id,
              name: node.data.label || '未命名 Agent',
              role: node.data.role || '',
              prompt: node.data.prompt || '',
              model: node.data.model || '',
              tools: node.data.tools || [],
            }))}
            routers={nodes.filter(n => n.type === 'router').map(node => ({
              id: node.id,
              name: node.data.label || '条件判断',
              model: node.data.model || '',  // 路由节点的模型配置
              conditions: (node.data.conditions || []).map((c: Condition) => ({
                id: c.id,
                name: c.name || '',
                condition: c.condition || '',
                target: c.target,
              })),
            }))}
            edges={edges}
            entryNodeVoiceConfig={(() => {
              const entryNode = nodes.find(n => n.type === 'entry')
              if (!entryNode?.data?.enableVoice) return undefined
              return {
                enableVoice: entryNode.data.enableVoice,
                asrProvider: entryNode.data.asrProvider,
              }
            })()}
            onNodeActivate={(nodeId) => {
              console.log('🎯 [ProjectStudio] onNodeActivate 被调用:', nodeId)
              setActiveNodeId(nodeId)
            }}
            onNodeVisited={(nodeId) => {
              console.log('👣 [ProjectStudio] onNodeVisited 被调用:', nodeId)
              setVisitedNodeIds(prev => {
                const newSet = new Set([...prev, nodeId])
                console.log('👣 [ProjectStudio] visitedNodeIds 更新:', Array.from(newSet))
                return newSet
              })
            }}
            onExecutionReset={() => {
              setActiveNodeId(null)
              setVisitedNodeIds(new Set())
            }}
          />
        )}

        {/* 右侧技能配置面板 */}
        {rightPanel === 'skills' && effectiveProjectId && (
          <ProjectSkillsPanel
            projectId={effectiveProjectId}
            onClose={() => setRightPanel('none')}
          />
        )}
      </div>

      {/* ==================== 对话框组件 ==================== */}
      <ProjectSettingsDialog
        open={showSettingsDialog}
        onOpenChange={setShowSettingsDialog}
        isLoadingApiKey={isLoadingApiKey}
        apiKeyData={apiKeyData}
        newlyGeneratedKey={newlyGeneratedKey}
        isRegenerating={isRegenerating}
        isDeleting={isDeleting}
        onCopyApiKey={handleCopyApiKey}
        onGenerateApiKey={handleGenerateApiKey}
        onRegenerateApiKey={() => setShowRegenerateConfirm(true)}
        onDeleteApiKey={() => setShowDeleteConfirm(true)}
        formatApiKey={formatApiKey}
      />

      <RegenerateConfirmDialog
        open={showRegenerateConfirm}
        onOpenChange={setShowRegenerateConfirm}
        isRegenerating={isRegenerating}
        onConfirm={handleGenerateApiKey}
      />

      <DeleteConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        isDeleting={isDeleting}
        onConfirm={handleDeleteApiKey}
      />
    </div>
    </ExecutionContext.Provider>
  )
}

// 导出包装了 ReactFlowProvider 的组件
export default function ProjectStudio() {
  return (
    <ReactFlowProvider>
      <FlowCanvas />
    </ReactFlowProvider>
  )
}

// v8.2: 导出 TestPanel 供设备测试使用
export { TestPanel }